/**
 * NetworkEditContext - Contexto para edición interactiva de red canónica
 *
 * @version 1.0
 * @created 2026-03-03
 * @module core/spatial/contexts
 *
 * @description
 * Gestiona el estado de edición interactiva de la red vial canónica:
 * - Crear ramas desde nodos existentes
 * - Dividir segmentos en punto medio
 * - Eliminar nodos (+ edges conectados en cascada)
 *
 * Arquitectura:
 * - Estado temporal con preview features (isPreview: true)
 * - Confirmaciones con NetworkEditConfirmation
 * - Integración con canonicalNetworkApi para CRUD
 *
 * @example
 * ```typescript
 * const { editMode, startBranchCreation, confirmBranchCreation } = useNetworkEdit();
 *
 * // Click en nodo
 * startBranchCreation(nodeId);
 *
 * // Click en mapa
 * createBranchPreview([lng, lat]);
 *
 * // Confirmar
 * confirmBranchCreation();
 * ```
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode, useSyncExternalStore } from 'react';
import type { CanonicalNode, CanonicalEdge, PointGeometry, LineStringGeometry, BranchIntent } from '../types';
import type { GeoJSONSource } from 'maplibre-gl';
import { mapEngine } from '../../map-engine/MapEngineService';
import { canonicalNetworkApi } from '../services/canonical-network-api.service';
import type { DeletionImpact } from '../services/canonical-network-api.service';
import { useProject } from '../../projects';
import { useToast } from '../../toast';
import { buildPreviewForMovedNode, recomputeEdgeGeometry, splitEdgeAtPoint, calculateMidPoint } from '../services/topology.service';
import { networkTransactionService } from '../services/network-transaction.service';
import { networkUndoRedoService, CreateBranchCommand, SplitEdgeAndCreateBranchCommand, MoveNodeCommand, EditEdgeGeometryCommand, InsertNodeOnEdgeCommand, MergeEdgesCommand } from '../services/network-undo-redo.service';
import type { MoveNodeStateSnapshot, MergeEdgesCommandData } from '../services/network-undo-redo.service';
import { canonicalNetworkStore, rawToFeature } from '../stores/canonical-network-store';
import type { CanonicalNodeFeature, CanonicalEdgeFeature, CanonicalFeature } from '../stores/canonical-network-store';
import { performanceProfiler } from '../services/performance-profiler.service';

// ═══════════════════════════════════════════════════════════════
// GEOMETRY HELPERS
// ═══════════════════════════════════════════════════════════════

/**
 * Calcula la distancia de un punto a un segmento de línea.
 * Usado para determinar en qué segmento insertar un nuevo shape point.
 */
function pointToSegmentDistance(
  point: [number, number],
  segStart: [number, number],
  segEnd: [number, number]
): number {
  const dx = segEnd[0] - segStart[0];
  const dy = segEnd[1] - segStart[1];
  const segLengthSq = dx * dx + dy * dy;

  if (segLengthSq === 0) {
    // Segmento degenerado (punto)
    const pdx = point[0] - segStart[0];
    const pdy = point[1] - segStart[1];
    return Math.sqrt(pdx * pdx + pdy * pdy);
  }

  // Parámetro t de la proyección
  const t = Math.max(0, Math.min(1,
    ((point[0] - segStart[0]) * dx + (point[1] - segStart[1]) * dy) / segLengthSq
  ));

  // Punto proyectado
  const projX = segStart[0] + t * dx;
  const projY = segStart[1] + t * dy;

  // Distancia al punto proyectado
  const distX = point[0] - projX;
  const distY = point[1] - projY;
  return Math.sqrt(distX * distX + distY * distY);
}

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

/**
 * Modo de edición activo (FIX: Hallazgo #4 - agregado delete-edge)
 */
export type NetworkEditMode = 'create-branch' | 'split-segment' | 'delete-node' | 'delete-edge' | 'move-node' | 'insert-node-on-edge' | 'draw-freehand-segment' | null;

/**
 * Estado del menú contextual de edge
 */
export interface EdgeContextMenuState {
  /** Posición del menú [lng, lat] */
  position: [number, number];
  /** ID del edge sobre el que se hizo click */
  edgeId: string;
}

/**
 * Estado del menú contextual de nodo
 */
export interface NodeContextMenuState {
  /** Posición del menú [lng, lat] */
  position: [number, number];
  /** ID del nodo sobre el que se hizo click */
  nodeId: string;
}

/**
 * Metadata para confirmación UI
 */
export interface NetworkEditConfirmationMetadata {
  nodeCount?: number;
  edgeCount?: number;
  message?: string;
  /** Impacto en asociaciones punto-red (para delete-edge y delete-node) */
  impact?: DeletionImpact;
}

/**
 * Estado de confirmación
 */
export interface NetworkEditConfirmation {
  mode: Exclude<NetworkEditMode, null>;
  position: [number, number]; // Screen coordinates
  metadata: NetworkEditConfirmationMetadata;
}

/**
 * Estado del contexto de edición de red
 */
export interface NetworkEditContextState {
  /** Modo de edición activo */
  editMode: NetworkEditMode;

  /** Nodo desde el cual se está creando rama */
  branchSourceNodeId: string | null;

  /** Preview temporal de nuevo nodo */
  previewNode: CanonicalNode | null;

  /** Preview temporal de nuevo edge */
  previewEdge: CanonicalEdge | null;

  /** Nodo confirmado localmente (pending backend confirmation) - UX improvement to avoid visual cut */
  pendingConfirmationNode: CanonicalNode | null;

  /** Edge confirmado localmente (pending backend confirmation) - UX improvement to avoid visual cut */
  pendingConfirmationEdge: CanonicalEdge | null;

  /** Segundo preview edge (solo para split-segment) */
  previewEdge2: CanonicalEdge | null;

  /** Preview edges para move-node (todos los edges conectados) */
  previewEdges: CanonicalEdge[];

  /** Edge being split */
  splitTargetEdgeId: string | null;

  /** Nodo seleccionado para edición (move/delete) */
  selectedNodeId: string | null;

  /** Nodo seleccionado para crear ramas */
  selectedNodeForBranch: string | null;

  /** Edge seleccionado para edición */
  selectedEdgeId: string | null;

  // REFACTOR: hoveredNodeId y hoveredEdgeId movidos a UrbanNetworkContext
  // Esto elimina la duplicación de estado entre contextos

  /** Target de snap durante creación de rama */
  snapTarget: { type: 'node' | 'edge'; id: string; snapPoint: [number, number] } | null;

  /** Confirmación activa */
  confirmation: NetworkEditConfirmation | null;

  /** Posición original del nodo antes de mover (para poder cancelar) */
  originalNodePosition: [number, number] | null;

  /** IDs de edges conectados al nodo que se está moviendo */
  connectedEdgeIds: string[] | null;

  /** Edges conectados completos (para no tener que buscarlos en cada mousemove) */
  connectedEdges: CanonicalEdge[] | null;

  /** Nodo completo que se está moviendo (para no tener que buscarlo en cada mousemove) */
  movingNode: CanonicalNode | null;

  /** Si está arrastrando activamente */
  isDragging: boolean;

  /** Menú contextual de edge abierto */
  edgeContextMenu: EdgeContextMenuState | null;

  /** Menú contextual de nodo abierto */
  nodeContextMenu: NodeContextMenuState | null;

  /** Edge sobre el cual se está insertando un nodo */
  insertNodeTargetEdgeId: string | null;

  // ── Edición Geométrica de Segmento ──

  /** ID del edge en edición geométrica (micro-nodos internos visibles) */
  geometryEditEdgeId: string | null;

  /** Geometría original del edge al iniciar edición (para undo) */
  geometryEditOriginalCoords: Array<[number, number]> | null;

  /** Índice del shape point siendo arrastrado (1..n-2, NO extremos) */
  draggingShapePointIndex: number | null;

  // ── Merge Edges (Fase 3) ──

  /** IDs de edges seleccionados para merge (máximo 2) */
  selectedEdgesForMerge: string[];

  /** Resultado de validación local para merge (solo UX, no autoritativa) */
  mergeValidationResult: { valid: boolean; reason?: string } | null;

  // ── Multi-Merge V1 (Fase 3) ──

  /** IDs de edges seleccionados para multi-merge (sin límite, N ≥ 2) */
  selectedEdgeIds: string[];

  /** Resultado de validación de cadena en tiempo real */
  chainValidation: {
    isValid: boolean;
    errorMessage: string | null;
    orderedEdgeIds: string[] | null;
    previewGeometry: LineStringGeometry | null;
  } | null;

  // ── Draw Freehand Segment (Etapa 1) ──

  /** Modo de dibujo de segmento a mano alzada activo */
  isDrawingFreehandSegment: boolean;

  /** Nodo origen del segmento multipunto */
  freehandSourceNodeId: string | null;

  /** Coordenadas del nodo origen (cache para performance) */
  freehandSourceCoords: [number, number] | null;

  /** Shape points intermedios acumulados (NO incluye extremos) */
  freehandIntermediatePoints: Array<[number, number]>;

  /** Preview de geometría acumulativa */
  freehandPreviewEdge: CanonicalEdge | null;
}

/**
 * Acciones del contexto de edición de red
 */
export interface NetworkEditContextActions {
  // ── Crear Rama ──
  /** Selecciona un nodo para crear ramas (muestra handles) */
  selectNodeForBranch: (nodeId: string | null) => void;

  /** Inicia creación de rama desde nodo seleccionado o nodeId proporcionado */
  startBranchCreation: (nodeId?: string) => void;

  /** Crea preview de nuevo nodo + edge */
  createBranchPreview: (coordinates: [number, number]) => void;

  /** Confirma y guarda rama en BD con intent explícito (FASE 6 - FIX: Hallazgo #5) */
  confirmBranchCreation: (intent: BranchIntent) => Promise<void>;

  // ── Dividir Segmento ──
  /** Inicia división de edge */
  startSegmentSplit: (edgeId: string, point: [number, number]) => void;

  /** Confirma y divide edge en 2 */
  confirmSegmentSplit: () => Promise<void>;

  // ── Eliminar Nodo ──
  /** Muestra confirmación de eliminación */
  promptDeleteNode: (nodeId: string, clickCoordinates?: [number, number]) => void;

  /** Confirma y elimina nodo + edges */
  confirmDeleteNode: () => Promise<void>;

  // ── Eliminar Edge (FIX: Hallazgo #4) ──
  /** Muestra confirmación de eliminación de edge */
  promptDeleteEdge: (edgeId: string, clickCoordinates?: [number, number]) => void;

  /** Confirma y elimina edge */
  confirmDeleteEdge: () => Promise<void>;

  // ── Merge Edges ──
  /**
   * Fusiona dos edges contiguos en uno solo
   *
   * @param edgeId1 - ID del primer edge a fusionar
   * @param edgeId2 - ID del segundo edge a fusionar
   * @returns Promise que se resuelve cuando la fusión se completa
   *
   * @remarks
   * La operación es undoable. El comando se registra en el stack de undo/redo.
   */
  mergeEdges: (edgeId1: string, edgeId2: string) => Promise<void>;

  // ── Menú Contextual de Edge ──
  /** Abre menú contextual para edge */
  openEdgeContextMenu: (edgeId: string, position: [number, number]) => void;

  /** Cierra menú contextual */
  closeEdgeContextMenu: () => void;

  // ── Menú Contextual de Nodo ──
  /** Abre menú contextual para nodo */
  openNodeContextMenu: (nodeId: string, position: [number, number]) => void;

  /** Cierra menú contextual de nodo */
  closeNodeContextMenu: () => void;

  // ── Insertar Nodo en Edge ──
  /** Inicia modo inserción de nodo sobre edge (ghost node sigue mouse) */
  startInsertNodeOnEdge: (edgeId: string) => void;

  /** Actualiza posición del ghost node durante inserción */
  updateInsertNodePosition: (position: [number, number]) => void;

  /** Confirma inserción de nodo dividiendo el edge */
  confirmInsertNode: () => Promise<void>;

  // ── Mover Nodo ──
  /** Inicia movimiento de nodo (drag start) */
  startNodeMove: (node: CanonicalNode, connectedEdges: CanonicalEdge[]) => void;

  /** Actualiza posición del nodo mientras se arrastra */
  updateNodePosition: (newPosition: [number, number]) => void;

  /** Confirma nueva posición del nodo */
  confirmNodeMove: () => Promise<void>;

  /** Cancela movimiento y vuelve a posición original */
  cancelNodeMove: () => void;

  // ── Activación de Modos ──
  /** Activa modo crear rama (esperando click en nodo) */
  activateCreateBranchMode: () => void;

  /** Activa modo dividir segmento (esperando click en edge) */
  activateSplitSegmentMode: () => void;

  /** Activa modo eliminar nodo (esperando click derecho en nodo) */
  activateDeleteNodeMode: () => void;

  /** Activa modo mover nodo (esperando drag de nodo) */
  activateMoveNodeMode: () => void;

  // ── General ──
  /** Cancela operación actual */
  cancelEdit: () => void;

  /** Reset completo del estado */
  reset: () => void;

  // REFACTOR: setHoveredNodeId y setHoveredEdgeId movidos a UrbanNetworkContext

  /** Set selección de nodo/edge (FASE 4: fuente única) */
  setSelectedNodeId: (nodeId: string | null) => void;
  setSelectedEdgeId: (edgeId: string | null) => void;

  /** Set snap target durante creación de rama */
  setSnapTarget: (target: { type: 'node' | 'edge'; id: string; snapPoint: [number, number] } | null) => void;

  // ── Undo/Redo ──
  /** Deshace la última operación */
  undo: () => Promise<void>;

  /** Rehace la última operación deshecha */
  redo: () => Promise<void>;

  /** Verifica si se puede deshacer */
  canUndo: () => boolean;

  /** Verifica si se puede rehacer */
  canRedo: () => boolean;

  // ── Edición Geométrica de Segmento ──
  /** Inicia edición geométrica de un edge (muestra micro-nodos internos) */
  startGeometryEdit: (edgeId: string) => void;

  /** Inicia drag de un shape point interno */
  startShapePointDrag: (shapePointIndex: number) => void;

  /** Actualiza posición de shape point durante drag */
  updateShapePointPosition: (newPosition: [number, number]) => void;

  /** Confirma edición geométrica (persiste y registra undo) */
  confirmGeometryEdit: () => Promise<void>;

  /** Cancela edición geométrica */
  cancelGeometryEdit: () => void;

  /**
   * Simplifica un edge a línea recta (elimina shape points internos)
   * Operación geométrica - NO altera topología
   * @param edgeId - ID del edge a simplificar
   */
  simplifyEdgeToLine: (edgeId: string) => Promise<void>;

  /**
   * Agrega un shape point geométrico al edge en edición.
   * Operación geométrica - NO altera topología, NO divide el edge.
   * @param position - Coordenadas [lng, lat] donde insertar el punto
   * @returns Índice del nuevo shape point, o null si falló
   */
  addShapePoint: (position: [number, number]) => Promise<number | null>;

  /**
   * Elimina un shape point geométrico del edge en edición.
   * Operación geométrica - NO altera topología.
   * No puede eliminar si quedarían menos de 2 puntos.
   * @param shapePointIndex - Índice del shape point a eliminar (1..n-2)
   */
  removeShapePoint: (shapePointIndex: number) => Promise<void>;

  /**
   * Actualiza los handles visuales para reflejar el estado actual.
   * Útil después de agregar/eliminar shape points.
   */
  refreshShapeHandles: () => void;

  // ── Merge Edges Selection (Fase 3) ──

  /**
   * Toggle edge para selección de merge con Shift+click
   *
   * @param edgeId - ID del edge a seleccionar/deseleccionar
   * @param shiftKey - Si se presionó Shift (para multi-selección)
   *
   * @remarks
   * Comportamiento:
   * - Sin Shift: reemplaza selección (solo este edge)
   * - Con Shift + primer edge: agrega a selección (máximo 2)
   * - Con Shift + segundo edge inválido: rechaza sin perder primero
   * - Con Shift + edge ya seleccionado: lo quita de selección
   */
  toggleEdgeForMerge: (edgeId: string, shiftKey: boolean) => void;

  /**
   * Limpia selección de edges para merge
   */
  clearMergeSelection: () => void;

  // ── Multi-Merge V1 (Fase 3) ──

  /**
   * Agrega un edge a la selección múltiple con validación en tiempo real
   *
   * @param edgeId - ID del edge a agregar
   *
   * @remarks
   * - Valida la cadena en tiempo real llamando a validateChain endpoint
   * - Si es inválido, NO agrega el edge y muestra tooltip con error
   * - Si es válido, actualiza chainValidation incluyendo previewGeometry
   */
  addEdgeToSelection: (edgeId: string) => Promise<void>;

  /**
   * Remueve un edge de la selección múltiple
   *
   * @param edgeId - ID del edge a remover
   */
  removeEdgeFromSelection: (edgeId: string) => void;

  /**
   * Limpia la selección múltiple completamente
   */
  clearSelection: () => void;

  /**
   * Confirma y ejecuta el multi-merge de todos los edges seleccionados
   *
   * @remarks
   * - Valida que chainValidation.isValid sea true
   * - Ejecuta mergeMultipleEdges en backend
   * - Actualiza store canónico
   * - Registra command para undo/redo
   * - Limpia selección
   * - Selecciona merged edge
   */
  confirmMultiMerge: () => Promise<void>;

  // ── Draw Freehand Segment (Etapa 1) ──

  /**
   * Inicia dibujo de segmento a mano alzada desde nodo
   * @param nodeId - ID del nodo origen
   */
  startFreehandSegmentDrawing: (nodeId: string) => void;

  /**
   * Agrega shape point intermedio (NO crea nodo topológico)
   * @param coords - Coordenadas [lng, lat] del shape point
   */
  addFreehandShapePoint: (coords: [number, number]) => void;

  /**
   * Actualiza preview durante mouse move
   * @param cursorCoords - Coordenadas actuales del cursor
   */
  updateFreehandPreview: (cursorCoords: [number, number]) => void;

  /**
   * Cancela dibujo de segmento a mano alzada
   */
  cancelFreehandSegmentDrawing: () => void;

  /**
   * Confirma y persiste segmento a mano alzada con geometría multipunto
   * @param intent - Intención de terminal: snap-node (nodo existente) o free-point (nuevo nodo)
   */
  confirmFreehandSegment: (intent: BranchIntent) => Promise<void>;
}

/**
 * Estado reactivo del historial de edición.
 */
export interface NetworkEditHistoryState {
  /** Hay cambios locales aplicados pendientes de commit */
  hasPendingNetworkChanges: boolean;

  /** Se puede deshacer ahora mismo */
  canUndoNow: boolean;

  /** Se puede rehacer ahora mismo */
  canRedoNow: boolean;

  /** Descripción legible de la próxima operación a deshacer */
  undoDescription: string | null;

  /** Descripción legible de la próxima operación a rehacer */
  redoDescription: string | null;
}

/**
 * Tipo completo del contexto
 */
export type NetworkEditContextType =
  NetworkEditContextState &
  NetworkEditHistoryState &
  NetworkEditContextActions;

// ═══════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════

const NetworkEditContext = createContext<NetworkEditContextType | null>(null);

// ═══════════════════════════════════════════════════════════════
// PROVIDER
// ═══════════════════════════════════════════════════════════════

interface NetworkEditProviderProps {
  children: ReactNode;
}

const INITIAL_STATE: NetworkEditContextState = {
  editMode: null,
  branchSourceNodeId: null,
  previewNode: null,
  previewEdge: null,
  pendingConfirmationNode: null,
  pendingConfirmationEdge: null,
  previewEdge2: null,
  previewEdges: [],
  splitTargetEdgeId: null,
  selectedNodeId: null,
  selectedNodeForBranch: null,
  selectedEdgeId: null,
  // REFACTOR: hoveredNodeId y hoveredEdgeId movidos a UrbanNetworkContext
  snapTarget: null,
  confirmation: null,
  originalNodePosition: null,
  connectedEdgeIds: null,
  connectedEdges: null,
  movingNode: null,
  isDragging: false,
  edgeContextMenu: null,
  nodeContextMenu: null,
  insertNodeTargetEdgeId: null,
  // Edición geométrica de segmento
  geometryEditEdgeId: null,
  geometryEditOriginalCoords: null,
  draggingShapePointIndex: null,
  // Merge edges (Fase 3)
  selectedEdgesForMerge: [],
  mergeValidationResult: null,
  // Multi-Merge V1 (Fase 3)
  selectedEdgeIds: [],
  chainValidation: null,
  // Draw Freehand Segment (Etapa 1)
  isDrawingFreehandSegment: false,
  freehandSourceNodeId: null,
  freehandSourceCoords: null,
  freehandIntermediatePoints: [],
  freehandPreviewEdge: null,
};

export function NetworkEditProvider({ children }: NetworkEditProviderProps): JSX.Element {
  // ── State ──────────────────────────────────────────────────
  const [state, setState] = useState<NetworkEditContextState>(INITIAL_STATE);
  const undoRedoState = useSyncExternalStore(
    (listener) => networkUndoRedoService.subscribe(listener),
    () => networkUndoRedoService.getState(),
    () => networkUndoRedoService.getState()
  );

  // Flag para evitar llamadas concurrentes durante confirmación
  const [isConfirming, setIsConfirming] = useState(false);
  // Ref síncrono para que createBranchPreview pueda verificar sin esperar render
  const isConfirmingRef = useRef(false);

  // Refs para edición geométrica - evitan stale closures durante drag
  const draggingShapePointIndexRef = useRef<number | null>(null);
  const geometryEditEdgeIdRef = useRef<string | null>(null);

  // Refs para throttling de actualizaciones durante drag (requestAnimationFrame)
  const pendingShapePointUpdateRef = useRef<number | null>(null);
  const latestShapePointPositionRef = useRef<[number, number] | null>(null);

  // PERF: Coordenadas del edge durante drag - NO se toca el store canónico durante drag
  const dragPreviewCoordsRef = useRef<Array<[number, number]> | null>(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // PERF: Cache de coordenadas del nodo origen para create-branch
  // Evita búsqueda O(n) en cada mousemove
  // ═══════════════════════════════════════════════════════════════════════════
  const branchSourceCoordsRef = useRef<[number, number] | null>(null);

  // Mantener refs sincronizados con state (para evitar stale closures en callbacks)
  useEffect(() => {
    draggingShapePointIndexRef.current = state.draggingShapePointIndex;
  }, [state.draggingShapePointIndex]);

  useEffect(() => {
    geometryEditEdgeIdRef.current = state.geometryEditEdgeId;
  }, [state.geometryEditEdgeId]);

  // ── Project ID & Toast ──────────────────────────────────────────────
  const { projectId } = useProject();
  const { showToast } = useToast();
  const hasPendingNetworkChanges = undoRedoState.undoCount > 0;
  const canUndoNow = undoRedoState.canUndo;
  const canRedoNow = undoRedoState.canRedo;

  const getDeletionActionLabel = useCallback((impact?: DeletionImpact): string => {
    if (!impact) {
      return 'eliminación';
    }

    if (impact.canDeleteWithAutoRepair && !impact.canDeleteDirectly) {
      return 'reparación y eliminación';
    }

    return 'eliminación';
  }, []);

  const getNetworkEditErrorMessage = useCallback((error: any, fallback: string): string => {
    const responseData = error?.response?.data;
    if (typeof responseData?.message === 'string') {
      return responseData.message;
    }
    if (typeof error?.message === 'string' && !error.message.startsWith('HTTP ')) {
      return error.message;
    }
    return fallback;
  }, []);

  // DEBUG: Log para diagnosticar BUG A (undo state en context)
  console.log('[NetworkEditContext] 📊 undoRedoState:', {
    canUndo: undoRedoState.canUndo,
    canRedo: undoRedoState.canRedo,
    undoCount: undoRedoState.undoCount,
    isExecuting: undoRedoState.isExecuting,
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // RESINCRONIZAR GEOMETRY EDIT DESPUÉS DE UNDO/REDO
  //
  // Cuando ocurre un undo/redo que afecta el edge en edición geométrica,
  // los comandos actualizan el store pero NO actualizan el estado de UI.
  // Este efecto detecta esos cambios y resincroniza handles y refs.
  // ═══════════════════════════════════════════════════════════════════════════
  const prevUndoCountRef = useRef(undoRedoState.undoCount);
  const prevRedoCountRef = useRef(undoRedoState.redoCount);

  useEffect(() => {
    const undoCountChanged = undoRedoState.undoCount !== prevUndoCountRef.current;
    const redoCountChanged = undoRedoState.redoCount !== prevRedoCountRef.current;

    // Actualizar refs de tracking
    prevUndoCountRef.current = undoRedoState.undoCount;
    prevRedoCountRef.current = undoRedoState.redoCount;

    // Si no hubo undo/redo, no hacer nada
    if (!undoCountChanged && !redoCountChanged) {
      return;
    }

    // Si no estamos en modo geometry edit, no hay nada que resincronizar
    const currentGeometryEditEdgeId = geometryEditEdgeIdRef.current;
    if (!currentGeometryEditEdgeId) {
      return;
    }

    console.log('[NetworkEditContext] 🔄 Undo/redo detectado durante geometry edit, actualizando state...');

    // Leer geometría actual del store (ya actualizada por el comando de undo/redo)
    const edgeFeature = canonicalNetworkStore.getEdge(currentGeometryEditEdgeId);
    if (!edgeFeature) {
      console.warn('[NetworkEditContext] ⚠️ Edge no encontrado en store después de undo/redo');
      return;
    }

    const newCoords = edgeFeature.geometry.coordinates as Array<[number, number]>;

    // Actualizar refs de preview (para que próximo drag use coords correctas)
    dragPreviewCoordsRef.current = newCoords.map(c => [...c] as [number, number]);

    // Actualizar state de baseline (para undo de próximas operaciones)
    setState(prev => ({
      ...prev,
      geometryEditOriginalCoords: newCoords.map(c => [...c] as [number, number]),
    }));

    // NOTA: NO actualizamos handles visuales aquí porque el comando
    // EditEdgeGeometryCommand.syncShapeHandles() ya lo hizo sincrónicamente.
    // Duplicar el setData causa flicker visual.

    console.log('[NetworkEditContext] ✅ State sincronizado:', newCoords.length, 'puntos');
  }, [undoRedoState.undoCount, undoRedoState.redoCount]);

  // ── Helper: Limpiar Feature-States ─────────────────────────

  /**
   * Limpia TODOS los feature-states de la red canónica (FASE 5.1)
   * Previene artefactos visuales (snapped, hover, hidden quedando activos)
   *
   * @remarks
   * Se llama en:
   * - Cambio de editMode
   * - Confirmación exitosa de operación
   * - Cancelación de operación
   *
   * REFACTOR: El hover ahora se maneja en UrbanNetworkContext, no aquí.
   * Esta función solo limpia los feature-states de MapLibre.
   */
  /**
   * Limpia feature-states específicos de forma selectiva
   *
   * IMPORTANTE: NO iterar sobre todos los features - eso causa freeze del canvas
   * debido al bombardeo de llamadas síncronas a removeFeatureState().
   *
   * En su lugar, solo limpiamos el snapTarget actual si existe.
   * Los estados de hover se gestionan en useCanonicalNetworkLayer.
   * Los estados de selected se gestionan por feature individual.
   */
  const cleanupFeatureStates = useCallback(() => {
    const map = mapEngine.getMap();
    if (!map) return;

    // Solo limpiar el snap target actual si existe
    if (state.snapTarget?.id) {
      map.removeFeatureState(
        { source: 'canonical-network', id: state.snapTarget.id },
        'snapped'
      );
      console.log('[NetworkEditContext] 🧹 Cleaned snapped state for:', state.snapTarget.id);
    }

    // NO limpiar todos los features - causa freeze del canvas
    // Los estados de hover/selected se gestionan en otros hooks
  }, [state.snapTarget]);

  // ── Acciones: Crear Rama ───────────────────────────────────

  /**
   * Selecciona un nodo para crear ramas
   * Click en nodo → lo selecciona y muestra handles
   */
  const selectNodeForBranch = useCallback((nodeId: string | null) => {
    console.log('[NetworkEditContext] selectNodeForBranch:', nodeId);
    console.log('[🔴 BRANCH_SOURCE_CHANGE] selectNodeForBranch setting branchSourceNodeId to:', nodeId);
    console.trace('[🔴 BRANCH_SOURCE_CHANGE] Stack trace:');

    setState((prev) => ({
      ...prev,
      selectedNodeForBranch: nodeId,
      // Limpiar estado de rama si se deselecciona
      branchSourceNodeId: nodeId,
      editMode: null,
      previewNode: null,
      previewEdge: null,
    }));

    // FASE 5.1: Limpiar feature-states al salir de modo
    cleanupFeatureStates();
  }, [cleanupFeatureStates]);

  /**
   * Inicia creación de rama desde nodo seleccionado o nodeId proporcionado
   * Click en nodo o drag desde handle → inicia el modo create-branch
   *
   * PERF: Cachea las coordenadas del nodo origen para evitar búsqueda O(n)
   * en cada llamada a createBranchPreview durante mousemove.
   */
  const startBranchCreation = useCallback((providedNodeId?: string) => {
    const nodeId = providedNodeId || state.selectedNodeForBranch;

    if (!nodeId) {
      console.warn('[NetworkEditContext] No node selected for branch');
      return;
    }

    // ═══════════════════════════════════════════════════════════════════════
    // PERF: Buscar y cachear coordenadas del nodo origen UNA SOLA VEZ
    // Esto evita la búsqueda O(n) en cada llamada a createBranchPreview
    // ═══════════════════════════════════════════════════════════════════════
    const map = mapEngine.getMap();
    if (map) {
      const source = map.getSource('canonical-network') as GeoJSONSource;
      if (source) {
        const sourceData = (source as any)._data as GeoJSON.FeatureCollection;
        if (sourceData?.features) {
          const nodeFeature = sourceData.features.find(
            (f) => f.properties?.id === nodeId && f.properties?.type === 'node'
          );
          if (nodeFeature && nodeFeature.geometry.type === 'Point') {
            branchSourceCoordsRef.current = nodeFeature.geometry.coordinates as [number, number];
            console.log('[NetworkEditContext] ✅ Cached branch source coords:', branchSourceCoordsRef.current);
          }
        }
      }
    }

    console.log('[NetworkEditContext] startBranchCreation from node:', nodeId);

    setState((prev) => ({
      ...prev,
      editMode: 'create-branch',
      branchSourceNodeId: nodeId,
      selectedNodeForBranch: nodeId,
      selectedNodeId: nodeId,
    }));
  }, [state.selectedNodeForBranch]);

  /**
   * Crea preview de nuevo nodo + edge
   * Si hay snapTarget, usa su posición (snap a nodo o edge existente)
   *
   * PERF OPTIMIZADO:
   * - Usa coordenadas cacheadas del nodo origen (branchSourceCoordsRef)
   * - Evita búsqueda O(n) en cada mousemove
   * - Trabajo pesado movido FUERA de setState
   * - Logs removidos del hot path
   *
   * @param coordinates - Coordenadas del cursor [lng, lat]
   * @param immediateSnapTarget - Snap target para usar directamente (evita problema de timing)
   */
  const createBranchPreview = useCallback(
    (coordinates: [number, number], immediateSnapTarget?: { type: string; id: string; snapPoint: [number, number] } | null) => {
      // ═══════════════════════════════════════════════════════════════════════
      // PERF: Validaciones y cálculos FUERA de setState
      // ═══════════════════════════════════════════════════════════════════════

      // Early return si estamos confirmando
      if (isConfirmingRef.current) {
        return;
      }

      // PERF: Usar coordenadas cacheadas (evita búsqueda O(n))
      const sourceCoordinates = branchSourceCoordsRef.current;
      if (!sourceCoordinates) {
        // Fallback: intentar obtener desde el source si cache está vacío
        // Esto solo debería pasar en edge cases
        console.warn('[createBranchPreview] ⚠️ Cache miss - branchSourceCoordsRef is null');
        return;
      }

      // Calcular target coordinates FUERA de setState
      const effectiveSnapTarget = immediateSnapTarget ?? null;
      const targetCoordinates = effectiveSnapTarget?.snapPoint || coordinates;

      // Determinar si crear preview node
      const shouldCreatePreviewNode = !effectiveSnapTarget || effectiveSnapTarget.type !== 'node';

      // Construir preview objects FUERA de setState
      const previewNode: Partial<CanonicalNode> | null = shouldCreatePreviewNode
        ? {
            id: 'preview-node',
            geometry: {
              type: 'Point',
              coordinates: targetCoordinates,
            } as PointGeometry,
          }
        : null;

      const previewEdge: Partial<CanonicalEdge> = {
        id: 'preview-edge',
        geometry: {
          type: 'LineString',
          coordinates: [sourceCoordinates, targetCoordinates],
        } as LineStringGeometry,
      };

      // ═══════════════════════════════════════════════════════════════════════
      // PERF: setState minimal - solo actualiza lo necesario
      // ═══════════════════════════════════════════════════════════════════════
      setState((currentState) => {
        // Quick validation
        if (currentState.editMode !== 'create-branch') {
          return currentState;
        }

        // ═══ PERF INSTRUMENTATION: Preview reappeared ═══
        console.timeEnd('[PERF] Preview blackout period');
        console.log('[PERF] T+Yms: Preview reappeared after mouse movement');

        // Return new state with preview
        return {
          ...currentState,
          previewNode: previewNode as CanonicalNode | null,
          previewEdge: previewEdge as CanonicalEdge,
        };
      });
    },
    [] // Sin dependencias - usa refs para valores que cambian
  );

  /**
   * Confirma y guarda rama en BD
   * Maneja 3 casos: free-point, snap-node, snap-edge
   * Usa transacciones con optimistic updates
   * @param intent - Intención explícita de creación (FIX: Hallazgo #5 - no infiere desde state.snapTarget)
   */
  const confirmBranchCreation = useCallback(async (intent: BranchIntent) => {
    // Evitar llamadas concurrentes
    if (isConfirming) {
      console.warn('[NetworkEditContext] ⚠️ Ignorando llamada duplicada - ya hay una confirmación en curso');
      return;
    }
    // NOTA: setIsConfirming(true) se llama DESPUÉS de las validaciones
    // para evitar dejar el flag en true si una validación falla tempranamente

    // ══════════════════════════════════════════════════════════════════
    // PERF: Iniciar sesión de profiling
    // ══════════════════════════════════════════════════════════════════
    const sessionId = performanceProfiler.startSession('create-branch', {
      intentType: intent.type,
      branchSourceNodeId: state.branchSourceNodeId,
    });

    const confirmStartTime = performance.now();
    console.log(`[CONFIRM] 🚀 Confirming creation at ${confirmStartTime.toFixed(2)}ms:`, {
      sessionId,
      intent,
      hasPreviewNode: !!state.previewNode,
      hasPreviewEdge: !!state.previewEdge,
      branchSourceNodeId: state.branchSourceNodeId,
    });

    // Crear geometrías desde intent
    let previewNode = state.previewNode;
    let previewEdge = state.previewEdge;

    if (intent && state.branchSourceNodeId) {
      // Buscar nodo origen en el mapa
      const map = mapEngine.getMap();
      if (!map) {
        console.error('[NetworkEditContext] Map not available');
        return;
      }

      const source = map.getSource('canonical-network') as GeoJSONSource;
      if (!source) {
        console.error('[NetworkEditContext] Source not found');
        return;
      }

      const sourceData = (source as any)._data as GeoJSON.FeatureCollection;
      const sourceNodeFeature = sourceData.features.find(
        (f) => f.properties?.id === state.branchSourceNodeId && f.properties?.type === 'node'
      );

      if (!sourceNodeFeature || sourceNodeFeature.geometry.type !== 'Point') {
        console.error('[NetworkEditContext] Source node not found');
        return;
      }

      const sourceCoordinates = sourceNodeFeature.geometry.coordinates as [number, number];

      // Usar coordenadas del intent (FIX: Hallazgo #5 - ya no infiere desde snapTarget)
      const finalCoordinates = intent.coords;

      console.log('[NetworkEditContext] Using coordinates for creation:', {
        intentType: intent.type,
        finalCoordinates,
      });

      // Crear geometrías localmente
      previewNode = {
        geometry: {
          type: 'Point',
          coordinates: finalCoordinates, // Usar coordenadas finales (snap o directas)
        },
        intersectionType: null,
      } as Partial<CanonicalNode>;

      previewEdge = {
        geometry: {
          type: 'LineString',
          coordinates: [sourceCoordinates, finalCoordinates], // Usar coordenadas finales
        },
      } as Partial<CanonicalEdge>;
    }

    // ── Validaciones de UX (Reglas 28-29) ──

    // Validar que no sea click sobre el mismo nodo origen (Regla 29)
    if (state.snapTarget?.type === 'node' && state.snapTarget.id === state.branchSourceNodeId) {
      console.warn('[NetworkEditContext] ❌ Cannot create edge to same source node (Regla 29)');
      alert('No se puede crear un segmento hacia el mismo nodo de origen.');
      return;
    }

    // Validar longitud mínima del segmento (Regla 28)
    if (previewEdge && previewEdge.geometry) {
      const coords = previewEdge.geometry.coordinates;
      if (coords.length === 2) {
        const [lng1, lat1] = coords[0];
        const [lng2, lat2] = coords[1];
        const dx = lng2 - lng1;
        const dy = lat2 - lat1;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Umbral: ~1 metro en coordenadas geo (aproximadamente 0.00001 grados)
        if (distance < 0.00001) {
          console.warn('[NetworkEditContext] ❌ Segment too short (Regla 28):', distance);
          alert('El segmento es demasiado corto. Mueve el cursor más lejos del nodo de origen.');
          return;
        }
      }
    }

    // Validar estado
    if (!previewEdge || !state.branchSourceNodeId || !projectId) {
      console.error('[NetworkEditContext] Cannot confirm: missing required data', {
        hasPreviewEdge: !!previewEdge,
        hasBranchSourceNodeId: !!state.branchSourceNodeId,
        hasProjectId: !!projectId,
      });
      return;
    }

    // ═══════════════════════════════════════════════════════════════════════
    // TOPOLOGÍA CORRECTA - Basada en intent explícito (FIX: Hallazgos #2, #5)
    // ═══════════════════════════════════════════════════════════════════════

    console.log('[NetworkEditContext] 🎯 Intent:', intent);

    // Marcar confirmación en curso DESPUÉS de todas las validaciones
    // Esto evita dejar isConfirming=true si una validación falla
    setIsConfirming(true);
    isConfirmingRef.current = true; // Ref síncrono para createBranchPreview

    // ═══ PERF INSTRUMENTATION: Start timing ═══
    console.time('[PERF] Preview blackout period');
    console.time('[PERF] API call duration');
    console.log('[PERF] T+0ms: User clicked, moving preview to pending confirmation');

    // UX IMPROVEMENT: Move preview to pending confirmation instead of clearing immediately
    // This prevents visual cut during API call
    setState((prev) => ({
      ...prev,
      pendingConfirmationNode: prev.previewNode,  // Preserve node as "pending"
      pendingConfirmationEdge: prev.previewEdge,  // Preserve edge as "pending"
      previewNode: null,  // Clear floating preview
      previewEdge: null,  // Clear floating edge preview
    }));
    console.log('[NetworkEditContext] Preview node AND edge moved to pending confirmation (visible during API call)');

    try {
      // Variables para resultados
      let newNode: CanonicalNode | null = null;
      let newEdges: CanonicalEdge[] = [];
      let targetNodeId: string = ''; // Se asignará en cada CASO
      let createBranchResult: import('../types').CreateBranchResult | null = null; // Para CASO 1 y 2

      // Variables para CASO 3 (snap-edge) - para undo/redo (FASE 6)
      let originalEdgeFeature: GeoJSON.Feature | undefined;
      // Propiedades del edge original (estructura plana del GeoJSON source)
      let originalEdgeProps: {
        id: string;
        fromNodeId: string;
        toNodeId: string;
        roadClass?: string;
      } | undefined;
      // Resultado normalizado para snap-edge (con branchEdge opcional)
      let normalizedResult: {
        splitNode: CanonicalNode;
        edge1: CanonicalEdge;
        edge2: CanonicalEdge;
        deletedEdgeId: string;
        branchEdge: {
          edge: CanonicalEdge | null;
          status: 'created' | 'omitted_duplicate';
        };
      } | null = null;

      // ═══ CASO 1: SNAP-NODE - Solo crear edge ═══
      if (intent.type === 'snap-node') {
        performanceProfiler.markStageStart(sessionId, 'case1-snap-node');
        console.log('[NetworkEditContext] 📍 CASO 1: Snap a nodo existente:', intent.nodeId);

        targetNodeId = intent.nodeId;

        // VALIDACIÓN: Verificar si ya existe un edge entre estos dos nodos
        const map = mapEngine.getMap();
        if (map) {
          const source = map.getSource('canonical-network') as maplibregl.GeoJSONSource;
          if (source) {
            const sourceData = (source as any)._data as GeoJSON.FeatureCollection;
            if (sourceData?.features) {
              const existingEdge = sourceData.features.find(
                (f) =>
                  f.properties?.type === 'edge' &&
                  (
                    (f.properties.fromNodeId === state.branchSourceNodeId && f.properties.toNodeId === targetNodeId) ||
                    (f.properties.fromNodeId === targetNodeId && f.properties.toNodeId === state.branchSourceNodeId)
                  )
              );

              if (existingEdge) {
                console.log('[NetworkEditContext] ⚠️ Edge duplicado detectado:', existingEdge.properties?.id);
                alert('Ya existe una conexión entre estos dos nodos. No se puede crear un segmento duplicado.');
                // Cancelar operación
                setState((prev) => ({
                  ...prev,
                  editMode: null,
                  branchSourceNodeId: null,
                  selectedNodeForBranch: null,
                  previewEdge: null,
                }));
                return;
              }
            }
          }
        }

        // Crear rama usando endpoint unificado (atomicidad garantizada)
        console.log('[NetworkEditContext] 📤 Llamando createBranch API (snap-node):', {
          intentType: 'snap-node',
          sourceNodeId: state.branchSourceNodeId,
          targetNodeId,
        });

        createBranchResult = await canonicalNetworkApi.createBranch(projectId, {
          intentType: 'snap-node',
          sourceNodeId: state.branchSourceNodeId,
          targetNodeId,
          edge: {
            geometry: {
              type: 'LineString',
              coordinates: [
                previewEdge.geometry.coordinates[0],
                intent.coords,
              ],
            },
            roadClass: 'residential',
            sourceType: 'manual',
          },
        });

        // Usar resultados del endpoint unificado
        newNode = createBranchResult.node;  // Nodo target (existente, no nuevo)
        newEdges = createBranchResult.edges;  // Array de edges creados
        console.log('[NetworkEditContext] ✅ createBranch response:', {
          nodeId: createBranchResult.node?.id,
          edgesCount: createBranchResult.edges.length,
          undoSnapshot: createBranchResult.undoSnapshot,
        });
        performanceProfiler.markStageEnd(sessionId, 'case1-snap-node', {
          edgeId: createBranchResult.edges[0]?.id,
          undoSnapshot: createBranchResult.undoSnapshot,
        });
      }

      // ═══ CASO 2: FREE-POINT - Crear nodo + edge ═══
      else if (intent.type === 'free-point') {
        performanceProfiler.markStageStart(sessionId, 'case2-free-point');
        console.log('[NetworkEditContext] 🆕 CASO 2: Crear nodo nuevo en punto libre');

        // Crear rama usando endpoint unificado (atomicidad garantizada)
        console.log('[NetworkEditContext] 📤 Llamando createBranch API (free-point):', {
          intentType: 'free-point',
          sourceNodeId: state.branchSourceNodeId,
          newNode: {
            geometry: { type: 'Point', coordinates: intent.coords },
            sourceType: 'manual',
            isManualAuthority: true,
          },
        });

        createBranchResult = await canonicalNetworkApi.createBranch(projectId, {
          intentType: 'free-point',
          sourceNodeId: state.branchSourceNodeId,
          newNode: {
            geometry: {
              type: 'Point',
              coordinates: intent.coords,
            },
            sourceType: 'manual',
            isManualAuthority: true,
          },
          edge: {
            geometry: previewEdge.geometry,
            roadClass: 'residential',
            sourceType: 'manual',
          },
        });

        // Usar resultados del endpoint unificado
        newNode = createBranchResult.node;  // Nodo creado
        newEdges = createBranchResult.edges;  // Array de edges creados
        targetNodeId = createBranchResult.node.id;

        console.log('[NetworkEditContext] ✅ createBranch response:', {
          nodeId: createBranchResult.node?.id,
          edgesCount: createBranchResult.edges.length,
          undoSnapshot: createBranchResult.undoSnapshot,
        });
        console.log('[NetworkEditContext] 📊 CASO 2 completado - newNode:', newNode?.id, 'newEdges:', newEdges.length);
        performanceProfiler.markStageEnd(sessionId, 'case2-free-point', {
          nodeId: createBranchResult.node?.id,
          edgeId: createBranchResult.edges[0]?.id,
          undoSnapshot: createBranchResult.undoSnapshot,
        });
      }

      // ═══ CASO 3: SNAP-EDGE - DIVIDIR edge (FIX: Bug crítico de integridad) ═══
      else if (intent.type === 'snap-edge') {
        performanceProfiler.markStageStart(sessionId, 'case3-snap-edge');
        console.log('[NetworkEditContext] ✂️ CASO 3: Snap a edge - DIVIDIR:', intent.edgeId);

        // 3.1: Buscar edge original en el mapa
        const map = mapEngine.getMap();
        if (!map) throw new Error('Map not available');

        const source = map.getSource('canonical-network') as GeoJSONSource;
        if (!source) throw new Error('Source not found');

        const sourceData = (source as any)._data as GeoJSON.FeatureCollection;
        originalEdgeFeature = sourceData.features.find(
          (f) => f.properties?.id === intent.edgeId && f.properties?.type === 'edge'
        );

        if (!originalEdgeFeature || originalEdgeFeature.geometry.type !== 'LineString') {
          throw new Error(`Edge original no encontrado: ${intent.edgeId}`);
        }

        // Extraer propiedades del edge (en el source están como propiedades planas, no objetos anidados)
        originalEdgeProps = originalEdgeFeature.properties as {
          id: string;
          fromNodeId: string;
          toNodeId: string;
          roadClass?: string;
        };

        // FIX: Validar que el edge tiene fromNodeId y toNodeId válidos
        if (!originalEdgeProps.fromNodeId || typeof originalEdgeProps.fromNodeId !== 'string') {
          throw new Error(`Edge ${intent.edgeId} no tiene fromNodeId válido: ${originalEdgeProps.fromNodeId}`);
        }
        if (!originalEdgeProps.toNodeId || typeof originalEdgeProps.toNodeId !== 'string') {
          throw new Error(`Edge ${intent.edgeId} no tiene toNodeId válido: ${originalEdgeProps.toNodeId}`);
        }

        // FIX: Validar roadClass - normalizar valores inválidos
        // Lista de valores válidos según backend (canonical-edge.entity.ts)
        const VALID_ROAD_CLASSES = new Set([
          'motorway', 'trunk', 'primary', 'secondary', 'tertiary',
          'residential', 'service', 'unclassified', 'pedestrian',
          'cycleway', 'footway'
        ]);

        const validRoadClass =
          originalEdgeProps.roadClass &&
          originalEdgeProps.roadClass.trim() !== '' &&
          VALID_ROAD_CLASSES.has(originalEdgeProps.roadClass)
            ? originalEdgeProps.roadClass
            : 'residential';

        console.log('[NetworkEditContext] 📋 Edge original:', {
          id: originalEdgeProps.id,
          from: originalEdgeProps.fromNodeId,
          to: originalEdgeProps.toNodeId,
          roadClass: originalEdgeProps.roadClass,
          validRoadClass,
        });

        // 3.2: Crear nodo split sobre el edge
        newNode = await canonicalNetworkApi.createNode(projectId, {
          geometry: {
            type: 'Point',
            coordinates: intent.coords,
          },
          sourceType: 'manual',
          intersectionType: 'simple', // Nodo split es simple
          isManualAuthority: true,
        });

        targetNodeId = newNode.id;
        console.log('[NetworkEditContext] ✅ Nodo split creado:', newNode.id);

        // 3.3: Crear 2 edges nuevos (edge original partido)
        // ⚠️ CRÍTICO: Crear edges ANTES de eliminar el original
        // Si falla la creación, el original permanece intacto
        const edgeCoords = originalEdgeFeature.geometry.coordinates as [number, number][];

        let edge1: any;
        let edge2: any;

        try {
          console.log('[NetworkEditContext] 🔄 Creando edge1:', {
            fromNodeId: originalEdgeProps.fromNodeId,
            toNodeId: newNode.id,
            roadClass: validRoadClass,
          });
          edge1 = await canonicalNetworkApi.createEdge(projectId, {
            fromNodeId: originalEdgeProps.fromNodeId,
            toNodeId: newNode.id,
            geometry: {
              type: 'LineString',
              coordinates: [edgeCoords[0], intent.coords],
            },
            sourceType: 'manual',
            roadClass: validRoadClass,
            parentEdgeId: intent.edgeId, // Track split lineage
          });
          console.log('[NetworkEditContext] ✅ Edge1 creado (fromNode→split):', edge1.id);

          console.log('[NetworkEditContext] 🔄 Creando edge2:', {
            fromNodeId: newNode.id,
            toNodeId: originalEdgeProps.toNodeId,
            roadClass: validRoadClass,
          });
          edge2 = await canonicalNetworkApi.createEdge(projectId, {
            fromNodeId: newNode.id,
            toNodeId: originalEdgeProps.toNodeId,
            geometry: {
              type: 'LineString',
              coordinates: [intent.coords, edgeCoords[edgeCoords.length - 1]],
            },
            sourceType: 'manual',
            roadClass: validRoadClass,
            parentEdgeId: intent.edgeId, // Track split lineage
          });
          console.log('[NetworkEditContext] ✅ Edge2 creado (split→toNode):', edge2.id);

          // 3.4: Solo AHORA eliminar edge original (después de que edges nuevos fueron creados exitosamente)
          await canonicalNetworkApi.deleteEdge(projectId, intent.edgeId);
          console.log('[NetworkEditContext] 🗑️ Edge original eliminado:', intent.edgeId);

        } catch (createEdgeError: any) {
          // ROLLBACK: Si falló la creación de algún edge, limpiar y preservar el original
          console.error('[NetworkEditContext] ❌ Error al crear edges del split:', createEdgeError);

          // Eliminar nodo split
          try {
            await canonicalNetworkApi.deleteNode(newNode.id);
            console.log('[NetworkEditContext] 🔄 ROLLBACK: Nodo split eliminado:', newNode.id);
          } catch (deleteNodeError) {
            console.error('[NetworkEditContext] ⚠️ Error al eliminar nodo split durante rollback:', deleteNodeError);
          }

          // Eliminar edge1 si fue creado
          if (edge1?.id) {
            try {
              await canonicalNetworkApi.deleteEdge(projectId, edge1.id);
              console.log('[NetworkEditContext] 🔄 ROLLBACK: Edge1 eliminado:', edge1.id);
            } catch (deleteEdge1Error) {
              console.error('[NetworkEditContext] ⚠️ Error al eliminar edge1 durante rollback:', deleteEdge1Error);
            }
          }

          // Eliminar edge2 si fue creado
          if (edge2?.id) {
            try {
              await canonicalNetworkApi.deleteEdge(projectId, edge2.id);
              console.log('[NetworkEditContext] 🔄 ROLLBACK: Edge2 eliminado:', edge2.id);
            } catch (deleteEdge2Error) {
              console.error('[NetworkEditContext] ⚠️ Error al eliminar edge2 durante rollback:', deleteEdge2Error);
            }
          }

          // Re-throw el error original con contexto
          throw new Error(`Failed to split edge: ${createEdgeError.message || createEdgeError}`);
        }

        // 3.5: Crear edge de rama (sourceNode → splitNode)
        console.log('[NetworkEditContext] 🔄 Creando branchEdge:', {
          fromNodeId: state.branchSourceNodeId,
          toNodeId: newNode.id,
        });

        // VALIDACIÓN: Verificar si el branchEdge sería duplicado de edge1 o edge2
        // Edge1: originalEdgeProps.fromNodeId → newNode.id
        // Edge2: newNode.id → originalEdgeProps.toNodeId
        const wouldBeDuplicateEdge1 =
          (state.branchSourceNodeId === originalEdgeProps.fromNodeId && newNode.id === newNode.id) ||
          (state.branchSourceNodeId === newNode.id && newNode.id === originalEdgeProps.fromNodeId);

        const wouldBeDuplicateEdge2 =
          (state.branchSourceNodeId === newNode.id && newNode.id === originalEdgeProps.toNodeId) ||
          (state.branchSourceNodeId === originalEdgeProps.toNodeId && newNode.id === newNode.id);

        if (wouldBeDuplicateEdge1 || wouldBeDuplicateEdge2) {
          console.log('[NetworkEditContext] ⚠️ Branch edge sería duplicado - se omite creación');
          newEdges.push(edge1, edge2);
        } else {
          // Crear branchEdge normalmente
          try {
            const branchEdge = await canonicalNetworkApi.createEdge(projectId, {
              fromNodeId: state.branchSourceNodeId,
              toNodeId: newNode.id,
              geometry: previewEdge.geometry,
              sourceType: 'manual',
              roadClass: 'residential',
            });
            console.log('[NetworkEditContext] ✅ Branch edge creado:', branchEdge.id);
            newEdges.push(edge1, edge2, branchEdge);
          } catch (branchError: any) {
            // Si falla por duplicado, solo guardar los edges del split
            if (branchError.message?.includes('Duplicate edge')) {
              console.log('[NetworkEditContext] ⚠️ Branch edge duplicado detectado por backend - solo se guardan edges del split');
              newEdges.push(edge1, edge2);
            } else {
              // Re-throw otros errores
              throw branchError;
            }
          }
        }

        // Normalizar resultado de la operación snap-edge
        const branchEdgeCreated = newEdges.length >= 3;
        normalizedResult = {
          splitNode: newNode!,
          edge1: newEdges[0],
          edge2: newEdges[1],
          deletedEdgeId: intent.edgeId,
          branchEdge: {
            edge: branchEdgeCreated ? newEdges[2] : null,
            status: branchEdgeCreated ? 'created' : 'omitted_duplicate',
          },
        };
        console.log('[NetworkEditContext] ✅ Resultado normalizado:', {
          branchEdgeStatus: normalizedResult.branchEdge.status,
          edgesCreated: newEdges.length,
        });

        performanceProfiler.markStageEnd(sessionId, 'case3-snap-edge', {
          nodeId: newNode?.id,
          edgeCount: newEdges.length,
        });
      }

      // ═══ ACTUALIZAR STORE CANÓNICO ═══
      performanceProfiler.markStageStart(sessionId, 'update-canonical-store');
      console.log('[NetworkEditContext] 🗃️ Actualizando store canónico con features nuevos');

      const storeFeaturesForMerge: CanonicalFeature[] = [];

      // Agregar nodo al store si se creó uno nuevo
      if (newNode) {
        const nodeFeature: CanonicalNodeFeature = {
          type: 'Feature',
          id: newNode.id,
          geometry: newNode.geometry,
          properties: {
            id: newNode.id,
            type: 'node',
            intersectionType: newNode.intersectionType,
            nodeVersion: newNode.nodeVersion,
          },
        };
        storeFeaturesForMerge.push(nodeFeature);
      }

      // Agregar todos los edges al store
      for (const edge of newEdges) {
        const fromNodeId = edge.fromNode?.id || (edge as any).fromNodeId || state.branchSourceNodeId;
        const toNodeId = edge.toNode?.id || (edge as any).toNodeId || targetNodeId;

        const edgeFeature: CanonicalEdgeFeature = {
          type: 'Feature',
          id: edge.id,
          geometry: edge.geometry,
          properties: {
            id: edge.id,
            type: 'edge',
            roadClass: edge.roadClass,
            fromNodeId,
            toNodeId,
            lengthMeters: edge.lengthMeters,
            edgeVersion: edge.edgeVersion,
          },
        };
        storeFeaturesForMerge.push(edgeFeature);
      }

      // Actualizar store con los nuevos features
      canonicalNetworkStore.merge(storeFeaturesForMerge);
      console.log('[NetworkEditContext] ✅ Store canónico actualizado:', canonicalNetworkStore.getStats());
      performanceProfiler.markStageEnd(sessionId, 'update-canonical-store', {
        featureCount: storeFeaturesForMerge.length,
      });

      // ═══ ACTUALIZAR MAPA (MANTENER CÓDIGO EXISTENTE) ═══
      performanceProfiler.markStageStart(sessionId, 'update-map-source');
      console.log('[NetworkEditContext] 🗺️ Actualizando mapa con features nuevos');

      const map = mapEngine.getMap();
      if (map) {
        const source = map.getSource('canonical-network') as GeoJSONSource;
        if (source) {
          const currentData = (source as any)._data as GeoJSON.FeatureCollection;

          // Los previews ahora están en un source separado, no necesitamos filtrar
          const newFeatures: GeoJSON.Feature[] = [...currentData.features];
          console.log('[NetworkEditContext] 📊 Features en source:', currentData.features.length, 'existentes');

          // Agregar nodo si se creó uno nuevo
          // NOTA: No incluir 'id' en el nivel del feature, solo en properties
          // porque el source usa promoteId: 'id' que lee de properties.id
          if (newNode) {
            newFeatures.push({
              type: 'Feature' as const,
              geometry: newNode.geometry,
              properties: {
                id: newNode.id,
                type: 'node',
                intersectionType: newNode.intersectionType,
              },
            });
          }

          // Agregar todos los edges creados
          for (const edge of newEdges) {
            // Nota: edge.fromNode/toNode pueden no estar incluidos en respuesta del backend
            // Usamos optional chaining y fallback
            const fromNodeId = edge.fromNode?.id || (edge as any).fromNodeId || state.branchSourceNodeId;
            const toNodeId = edge.toNode?.id || (edge as any).toNodeId || targetNodeId;

            console.log('[NetworkEditContext] 📍 Agregando edge al mapa:', {
              edgeId: edge.id,
              fromNodeId,
              toNodeId,
              roadClass: edge.roadClass,
            });

            // NOTA: No incluir 'id' en el nivel del feature, solo en properties
            // porque el source usa promoteId: 'id' que lee de properties.id
            newFeatures.push({
              type: 'Feature' as const,
              geometry: edge.geometry,
              properties: {
                id: edge.id,
                type: 'edge',
                roadClass: edge.roadClass,
                fromNodeId,
                toNodeId,
                lengthMeters: edge.lengthMeters,
              },
            });
          }

          // Si es snap-edge, remover el edge original del mapa Y del store (ya fue eliminado en BD)
          if (intent.type === 'snap-edge') {
            // Remover del store
            canonicalNetworkStore.remove([intent.edgeId]);
            console.log('[NetworkEditContext] 🗑️ Edge original removido del store:', intent.edgeId);

            // Remover del mapa
            const filteredFeatures = newFeatures.filter((f) => f.properties?.id !== intent.edgeId);
            source.setData({
              type: 'FeatureCollection',
              features: filteredFeatures,
            });
            console.log('[NetworkEditContext] 🗑️ Edge original removido del mapa:', intent.edgeId);
          } else {
            source.setData({
              type: 'FeatureCollection',
              features: newFeatures,
            });
          }

          // Verificar estado del source después del setData
          const afterSetData = (source as any)._data as GeoJSON.FeatureCollection;

          // CRÍTICO: Verificar que el nodo target existe en el source
          const targetNodeExists = afterSetData.features.some(
            (f) => f.properties?.id === targetNodeId && f.properties?.type === 'node'
          );

          console.log('[NetworkEditContext] ✅ Mapa actualizado con features reales:', {
            totalFeatures: afterSetData.features.length,
            lastFeatureIds: afterSetData.features.slice(-3).map((f: any) => f.properties?.id),
            targetNodeId,
            targetNodeExists,
            nodeCount: afterSetData.features.filter((f) => f.properties?.type === 'node').length,
          });

          if (!targetNodeExists) {
            console.error('[NetworkEditContext] ❌ CRITICAL: Target node NOT found in source after setData!', {
              targetNodeId,
              newNodeId: newNode?.id,
              intentType: intent.type,
              addedFeatureIds: newFeatures.filter((f) => f.properties?.type === 'node').map((f) => f.properties?.id),
              allNodeIds: afterSetData.features.filter((f) => f.properties?.type === 'node').map((f) => f.properties?.id).slice(-10),
            });
          }

          // CRÍTICO: Limpiar preview source INMEDIATAMENTE para evitar que features
          // nuevos aparezcan con estilo de preview (cyan) en lugar de estilo normal (blanco)
          const previewSource = map.getSource('canonical-network-preview') as GeoJSONSource;
          if (previewSource) {
            previewSource.setData({
              type: 'FeatureCollection',
              features: [],
            });
            console.log('[NetworkEditContext] 🧹 Preview source cleared immediately');
          }

          // ═══ PERF INSTRUMENTATION: API call completed ═══
          console.timeEnd('[PERF] API call duration');
          console.log('[PERF] T+Xms: API call completed successfully');

          // Forzar repaint del mapa para asegurar que los nuevos features se rendericen
          performanceProfiler.markStageStart(sessionId, 'map-repaint');
          map.triggerRepaint();
          console.log('[NetworkEditContext] 🎨 Map repaint triggered');
          performanceProfiler.markStageEnd(sessionId, 'map-repaint');

          // UX IMPROVEMENT: Clear pending confirmation after successful API call
          setState((prev) => ({
            ...prev,
            pendingConfirmationNode: null,
            pendingConfirmationEdge: null,
          }));
          console.log('[NetworkEditContext] Pending confirmation (node + edge) cleared after success');

          // Segunda limpieza del preview con delay para garantizar sincronización
          setTimeout(() => {
            const previewSourceDelayed = map.getSource('canonical-network-preview') as GeoJSONSource;
            if (previewSourceDelayed) {
              previewSourceDelayed.setData({
                type: 'FeatureCollection',
                features: [],
              });
              map.triggerRepaint();
              console.log('[NetworkEditContext] 🧹 Preview source cleared (delayed)');
            }
          }, 50);
        }
      }
      performanceProfiler.markStageEnd(sessionId, 'update-map-source');

      // ═══ REGISTRAR COMANDO PARA UNDO/REDO (FASE 6) ═══
      performanceProfiler.markStageStart(sessionId, 'register-undo-command');
      if (intent.type === 'snap-edge') {
        // Operación compuesta: dividir edge + crear rama
        // Aserciones de tipo: estas variables están definidas en el CASO 3
        if (!originalEdgeFeature || !originalEdgeProps || !normalizedResult) {
          throw new Error('Missing data for SplitEdgeAndCreateBranchCommand');
        }

        // Obtener geometría del sourceNode para redo
        const sourceNodeFeature = map?.querySourceFeatures('canonical-network').find(
          (f) => f.properties?.id === state.branchSourceNodeId && f.properties?.type === 'node'
        );
        const sourceNodeGeometry = sourceNodeFeature?.geometry as { type: 'Point'; coordinates: [number, number] } | undefined;

        if (!sourceNodeGeometry) {
          console.warn('[NetworkEditContext] Source node geometry not found for undo/redo');
        }

        const command = new SplitEdgeAndCreateBranchCommand({
          projectId,
          sourceNodeId: state.branchSourceNodeId,
          sourceNodeGeometry: sourceNodeGeometry || { type: 'Point', coordinates: previewEdge.geometry.coordinates[0] },
          originalEdgeId: normalizedResult.deletedEdgeId,
          originalEdgeGeometry: originalEdgeFeature.geometry as { type: 'LineString'; coordinates: [number, number][] },
          originalFromNodeId: originalEdgeProps.fromNodeId,
          originalToNodeId: originalEdgeProps.toNodeId,
          originalRoadClass: (originalEdgeProps.roadClass || 'residential') as string,
          splitNodeId: normalizedResult.splitNode.id,
          splitNodeGeometry: normalizedResult.splitNode.geometry,
          edge1Id: normalizedResult.edge1.id,
          edge2Id: normalizedResult.edge2.id,
          branchEdge: {
            id: normalizedResult.branchEdge.edge?.id || null,
            geometry: (normalizedResult.branchEdge.edge?.geometry as { type: 'LineString'; coordinates: [number, number][] }) || null,
            status: normalizedResult.branchEdge.status,
          },
        });
        networkUndoRedoService.registerCommand(command);
        console.log('[NetworkEditContext] ✅ SplitEdgeAndCreateBranchCommand registered for undo');
      } else {
        // Operación simple: crear rama (free-point o snap-node)
        // Validar que tenemos el result de createBranch
        if (!createBranchResult) {
          throw new Error('createBranchResult is null - should not happen for snap-node or free-point');
        }

        // Construir command con nueva estructura que usa undoSnapshot del backend
        const commandData: import('../services/network-undo-redo.service').CreateBranchCommandData = {
          projectId,
          intentType: intent.type as 'snap-node' | 'free-point',
          sourceNodeId: state.branchSourceNodeId,
          edgeData: {
            geometry: previewEdge.geometry,
            roadClass: 'residential',
            sourceType: 'manual',
          },
          undoSnapshot: createBranchResult.undoSnapshot,
        };

        // Agregar campos específicos según intent type
        if (intent.type === 'snap-node') {
          commandData.targetNodeId = targetNodeId;
        } else if (intent.type === 'free-point') {
          commandData.newNodeData = {
            geometry: {
              type: 'Point',
              coordinates: intent.coords,
            },
            sourceType: 'manual',
            isManualAuthority: true,
          };
        }

        const command = new CreateBranchCommand(commandData);
        networkUndoRedoService.registerCommand(command);
        console.log('[NetworkEditContext] ✅ CreateBranchCommand registered with undoSnapshot:', {
          intentType: intent.type,
          undoSnapshot: createBranchResult.undoSnapshot,
        });
      }
      performanceProfiler.markStageEnd(sessionId, 'register-undo-command');

      // 6. Continuar en modo creación desde el nuevo nodo
      performanceProfiler.markStageStart(sessionId, 'continue-mode');
      // El nodo recién creado se convierte en el origen para el siguiente

      // PERF: Actualizar cache de coordenadas para el nuevo nodo origen
      // intent.coords contiene las coordenadas del target (nuevo origen para chain)
      branchSourceCoordsRef.current = intent.coords;

      console.log('[🔵 BRANCH_SOURCE_CHANGE] confirmBranchCreation setting branchSourceNodeId to:', targetNodeId);
      setState((prev) => ({
        ...prev,
        previewNode: null,
        previewEdge: null,
        snapTarget: null,
        branchSourceNodeId: targetNodeId, // El nuevo nodo es ahora el origen
        selectedNodeId: targetNodeId,
        selectedNodeForBranch: targetNodeId,
        editMode: 'create-branch', // Mantener en modo creación
      }));

      // FASE 5.1: Limpiar feature-states (solo snap target, no todos)
      cleanupFeatureStates();

      performanceProfiler.markStageEnd(sessionId, 'continue-mode');

      const confirmEndTime = performance.now();
      console.log(`[CONFIRM] ✅ Creation completed in ${(confirmEndTime - confirmStartTime).toFixed(2)}ms - continuing from:`, targetNodeId);

      // ══════════════════════════════════════════════════════════════════
      // PERF: Finalizar sesión de profiling
      // ══════════════════════════════════════════════════════════════════
      performanceProfiler.endSession(sessionId, {
        success: true,
        targetNodeId,
        edgeCount: newEdges.length,
      });
    } catch (error: any) {
      console.error('[NetworkEditContext] ❌ Error creating branch:', error);
      console.error('[NetworkEditContext] ❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      // Limpiar estado (sin rollback - las operaciones ya fallaron en el backend)
      console.log('[🟠 BRANCH_SOURCE_CHANGE] confirmBranchCreation ERROR - setting branchSourceNodeId to null');

      // PERF: Limpiar cache de coordenadas
      branchSourceCoordsRef.current = null;

      setState((prev) => ({
        ...prev,
        previewNode: null,
        previewEdge: null,
        pendingConfirmationNode: null,  // UX: Clear pending on error
        pendingConfirmationEdge: null,  // UX: Clear pending on error
        snapTarget: null,
        branchSourceNodeId: null,
        selectedNodeId: null,
        selectedNodeForBranch: null,
        editMode: null,
      }));
      console.log('[NetworkEditContext] Pending confirmation (node + edge) cleared after error');

      // FASE 5.1: Limpiar feature-states (solo snap target, no todos)
      cleanupFeatureStates();

      // PERF: Finalizar sesión con error
      performanceProfiler.endSession(sessionId, {
        success: false,
        error: error.message,
      });

      alert(`Error al crear rama: ${error.message}. Ver consola para detalles.`);
    } finally {
      // SIEMPRE liberar flag de confirmación, incluso si hay error
      setIsConfirming(false);
      isConfirmingRef.current = false;
    }
  }, [state.previewNode, state.previewEdge, state.branchSourceNodeId, projectId, isConfirming]);

  // ── Acciones: Dividir Segmento ─────────────────────────────

  /**
   * Inicia división de edge usando función pura de topology service
   */
  const startSegmentSplit = useCallback((edgeId: string, splitPoint: [number, number]) => {
    console.log('[NetworkEditContext] startSegmentSplit:', edgeId, splitPoint);

    // Obtener edge desde el source GeoJSON
    const map = mapEngine.getMap();
    if (!map) {
      console.warn('[NetworkEditContext] Map not available');
      return;
    }

    const source = map.getSource('canonical-network') as GeoJSONSource;
    if (!source) {
      console.warn('[NetworkEditContext] Source not found');
      return;
    }

    const sourceData = (source as any)._data as GeoJSON.FeatureCollection;
    if (!sourceData || !sourceData.features) {
      console.warn('[NetworkEditContext] Invalid source data');
      return;
    }

    // Buscar edge en el source
    const edgeFeature = sourceData.features.find(
      (f) => f.properties?.id === edgeId && f.properties?.type === 'edge'
    );

    if (!edgeFeature || edgeFeature.geometry.type !== 'LineString') {
      console.warn('[NetworkEditContext] Edge not found or invalid geometry:', edgeId);
      return;
    }

    // Buscar fromNode y toNode
    const fromNodeId = edgeFeature.properties?.fromNodeId;
    const toNodeId = edgeFeature.properties?.toNodeId;

    const fromNodeFeature = sourceData.features.find(
      (f) => f.properties?.id === fromNodeId && f.properties?.type === 'node'
    );
    const toNodeFeature = sourceData.features.find(
      (f) => f.properties?.id === toNodeId && f.properties?.type === 'node'
    );

    if (!fromNodeFeature || !toNodeFeature) {
      console.warn('[NetworkEditContext] From/To nodes not found');
      return;
    }

    // Construir edge completo para splitEdgeAtPoint
    const edge: CanonicalEdge = {
      id: edgeId,
      geometry: edgeFeature.geometry as LineStringGeometry,
      fromNode: {
        id: fromNodeId,
        geometry: fromNodeFeature.geometry as PointGeometry,
      } as CanonicalNode,
      toNode: {
        id: toNodeId,
        geometry: toNodeFeature.geometry as PointGeometry,
      } as CanonicalNode,
      lengthMeters: edgeFeature.properties?.lengthMeters || 0,
      roadClass: edgeFeature.properties?.roadClass || null,
      accessMode: edgeFeature.properties?.accessMode || null,
      oneway: edgeFeature.properties?.oneway || false,
      onewayDirection: edgeFeature.properties?.onewayDirection || null,
      surfaceType: edgeFeature.properties?.surfaceType || null,
    } as CanonicalEdge;

    // Usar función pura para calcular split
    const splitResult = splitEdgeAtPoint(edge, splitPoint);

    console.log('[NetworkEditContext] Split calculated:', splitResult);

    // Crear preview features a partir del resultado
    const previewNode: Partial<CanonicalNode> = {
      id: 'preview-split-node',
      geometry: splitResult.newNode.geometry,
    };

    const previewEdge1: Partial<CanonicalEdge> = {
      id: 'preview-split-edge-1',
      geometry: splitResult.edge1.geometry,
    };

    const previewEdge2: Partial<CanonicalEdge> = {
      id: 'preview-split-edge-2',
      geometry: splitResult.edge2.geometry,
    };

    setState((prev) => ({
      ...prev,
      editMode: 'split-segment',
      splitTargetEdgeId: edgeId,
      selectedEdgeId: edgeId,
      previewNode: previewNode as CanonicalNode,
      previewEdge: previewEdge1 as CanonicalEdge,
      previewEdge2: previewEdge2 as CanonicalEdge,
    }));
  }, []);

  /**
   * Confirma y divide edge en 2
   */
  const confirmSegmentSplit = useCallback(async () => {
    console.log('[NetworkEditContext] confirmSegmentSplit');

    // Validar estado
    if (!state.previewNode || !state.previewEdge || !state.previewEdge2 || !state.splitTargetEdgeId || !projectId) {
      console.error('[NetworkEditContext] Cannot confirm split: missing required data', {
        hasPreviewNode: !!state.previewNode,
        hasPreviewEdge: !!state.previewEdge,
        hasPreviewEdge2: !!state.previewEdge2,
        hasSplitTargetEdgeId: !!state.splitTargetEdgeId,
        hasProjectId: !!projectId,
      });
      return;
    }

    try {
      console.log('[NetworkEditContext] ⏳ Splitting segment...');

      // 1. Obtener edge original para obtener fromNodeId y toNodeId
      const originalEdge = await canonicalNetworkApi.getEdge(state.splitTargetEdgeId);
      console.log('[NetworkEditContext] Original edge:', originalEdge);

      // 2. Crear nodo en el punto de split
      const splitNode = await canonicalNetworkApi.createNode(projectId, {
        geometry: state.previewNode.geometry,
        sourceType: 'manual',
        isManualAuthority: true,
      });

      console.log('[NetworkEditContext] ✅ Split node created:', splitNode.id);

      // 3. Crear primer edge (fromNode → splitNode)
      const newEdge1 = await canonicalNetworkApi.createEdge(projectId, {
        fromNodeId: originalEdge.fromNode.id,
        toNodeId: splitNode.id,
        geometry: state.previewEdge.geometry,
        sourceType: 'manual',
        roadClass: originalEdge.roadClass ?? undefined,
        accessMode: originalEdge.accessMode ?? undefined,
        onewayDirection: originalEdge.onewayDirection ?? undefined,
        surfaceType: originalEdge.surfaceType ?? undefined,
      });

      console.log('[NetworkEditContext] ✅ First edge created:', newEdge1.id);

      // 4. Crear segundo edge (splitNode → toNode)
      const newEdge2 = await canonicalNetworkApi.createEdge(projectId, {
        fromNodeId: splitNode.id,
        toNodeId: originalEdge.toNode.id,
        geometry: state.previewEdge2.geometry,
        sourceType: 'manual',
        roadClass: originalEdge.roadClass ?? undefined,
        accessMode: originalEdge.accessMode ?? undefined,
        onewayDirection: originalEdge.onewayDirection ?? undefined,
        surfaceType: originalEdge.surfaceType ?? undefined,
      });

      console.log('[NetworkEditContext] ✅ Second edge created:', newEdge2.id);

      // 5. Eliminar edge original
      await canonicalNetworkApi.deleteEdge(projectId, state.splitTargetEdgeId);
      console.log('[NetworkEditContext] ✅ Original edge deleted:', state.splitTargetEdgeId);

      // 6. Actualizar source del mapa
      const map = mapEngine.getMap();
      if (map) {
        const source = map.getSource('canonical-network') as GeoJSONSource;
        if (source) {
          const currentData = (source as any)._data as GeoJSON.FeatureCollection;

          // Remover preview features y edge original
          // NOTA: Usar properties.id para filtrar porque el source usa promoteId: 'id'
          const filteredFeatures = currentData.features.filter(
            (f) => !f.properties?.isPreview && f.properties?.id !== state.splitTargetEdgeId
          );

          // Agregar nuevos features reales
          // NOTA: No incluir 'id' en el nivel del feature, solo en properties
          const newFeatures = [
            ...filteredFeatures,
            {
              type: 'Feature' as const,
              geometry: splitNode.geometry,
              properties: {
                id: splitNode.id,
                type: 'node',
                intersectionType: splitNode.intersectionType,
              },
            },
            {
              type: 'Feature' as const,
              geometry: newEdge1.geometry,
              properties: {
                id: newEdge1.id,
                type: 'edge',
                roadClass: newEdge1.roadClass,
                fromNodeId: newEdge1.fromNode.id,
                toNodeId: newEdge1.toNode.id,
                lengthMeters: newEdge1.lengthMeters,
              },
            },
            {
              type: 'Feature' as const,
              geometry: newEdge2.geometry,
              properties: {
                id: newEdge2.id,
                type: 'edge',
                roadClass: newEdge2.roadClass,
                fromNodeId: newEdge2.fromNode.id,
                toNodeId: newEdge2.toNode.id,
                lengthMeters: newEdge2.lengthMeters,
              },
            },
          ];

          source.setData({
            type: 'FeatureCollection',
            features: newFeatures,
          });

          console.log('[NetworkEditContext] ✅ Map source updated');
        }
      }

      // 7. Limpiar estado
      setState((prev) => ({
        ...prev,
        previewNode: null,
        previewEdge: null,
        previewEdge2: null,
        splitTargetEdgeId: null,
        selectedEdgeId: null,
        editMode: null,
      }));

      console.log('[NetworkEditContext] ✅ Segment split completed');
    } catch (error) {
      console.error('[NetworkEditContext] ❌ Error splitting segment:', error);
      alert('Error al dividir segmento. Ver consola para detalles.');
    }
  }, [state.previewNode, state.previewEdge, state.previewEdge2, state.splitTargetEdgeId, projectId]);

  // ── Acciones: Eliminar Nodo ────────────────────────────────

  /**
   * Muestra confirmación de eliminación
   */
  const promptDeleteNode = useCallback(async (nodeId: string, clickCoordinates?: [number, number]) => {
    console.log('[NetworkEditContext] promptDeleteNode:', nodeId, 'clickCoordinates:', clickCoordinates);

    // Obtener nodo desde el source GeoJSON
    const map = mapEngine.getMap();
    if (!map) {
      console.warn('[NetworkEditContext] Map not available');
      return;
    }

    const source = map.getSource('canonical-network') as GeoJSONSource;
    if (!source) {
      console.warn('[NetworkEditContext] Source not found');
      return;
    }

    const sourceData = (source as any)._data as GeoJSON.FeatureCollection;
    if (!sourceData || !sourceData.features) {
      console.warn('[NetworkEditContext] Invalid source data');
      return;
    }

    // Buscar nodo en el source
    const nodeFeature = sourceData.features.find(
      (f) => f.properties?.id === nodeId && f.properties?.type === 'node'
    );

    if (!nodeFeature || nodeFeature.geometry.type !== 'Point') {
      console.warn('[NetworkEditContext] Node not found:', nodeId);
      return;
    }

    const nodeCoordinates = nodeFeature.geometry.coordinates as [number, number];

    // Usar coordenadas del click si se proporcionaron, si no usar las del nodo
    const confirmationCoordinates = clickCoordinates || nodeCoordinates;

    // Contar edges conectados (donde fromNodeId o toNodeId === nodeId)
    const connectedEdges = sourceData.features.filter(
      (f) =>
        f.properties?.type === 'edge' &&
        (f.properties?.fromNodeId === nodeId || f.properties?.toNodeId === nodeId)
    );

    const edgeCount = connectedEdges.length;
    console.log('[NetworkEditContext] Node has', edgeCount, 'connected edges');

    // Obtener impacto de eliminación (asociaciones afectadas)
    let impact: DeletionImpact | undefined;
    try {
      impact = await canonicalNetworkApi.getNodeDeletionImpact(nodeId);
      console.log('[NetworkEditContext] Node deletion impact:', impact);
    } catch (error) {
      console.warn('[NetworkEditContext] Could not fetch node deletion impact:', error);
    }

    // Crear metadata para la confirmación
    const metadata: NetworkEditConfirmationMetadata = {
      edgeCount,
      message: impact?.canDeleteWithAutoRepair && !impact.canDeleteDirectly
        ? '¿Reparar asociaciones y eliminar nodo?'
        : '¿Eliminar nodo?',
      impact,
    };

    setState((prev) => ({
      ...prev,
      editMode: 'delete-node',
      selectedNodeId: nodeId,
      // Crear un nodo preview para poder posicionar la confirmación
      previewNode: {
        id: nodeId,
        geometry: {
          type: 'Point',
          coordinates: nodeCoordinates,
        },
      } as CanonicalNode,
      // Guardar metadata en confirmation
      confirmation: {
        mode: 'delete-node',
        position: confirmationCoordinates, // Coordenadas geográficas (se proyectarán en NetworkEditConfirmation)
        metadata,
      },
    }));
  }, []);

  /**
   * Confirma y elimina nodo + edges
   */
  const confirmDeleteNode = useCallback(async () => {
    console.log('[NetworkEditContext] confirmDeleteNode');

    // Validar estado
    if (!state.selectedNodeId) {
      console.error('[NetworkEditContext] Cannot confirm delete: no node selected');
      return;
    }

    try {
      console.log('[NetworkEditContext] ⏳ Deleting node and connected edges...');

      // 1. Obtener edges conectados antes de eliminar (para actualizar UI)
      const map = mapEngine.getMap();
      let connectedEdgeIds: string[] = [];

      if (map) {
        const source = map.getSource('canonical-network') as GeoJSONSource;
        if (source) {
          const currentData = (source as any)._data as GeoJSON.FeatureCollection;
          const connectedEdges = currentData.features.filter(
            (f) =>
              f.properties?.type === 'edge' &&
              (f.properties?.fromNodeId === state.selectedNodeId ||
                f.properties?.toNodeId === state.selectedNodeId)
          );
          connectedEdgeIds = connectedEdges.map((e) => e.properties?.id as string);
        }
      }

      console.log('[NetworkEditContext] Edges to be deleted:', connectedEdgeIds);

      const impact = state.confirmation?.metadata?.impact;
      const repairStrategy =
        impact?.canDeleteWithAutoRepair && !impact.canDeleteDirectly ? 'auto' : undefined;

      // 2. Eliminar nodo (el backend eliminará edges en cascada)
      await canonicalNetworkApi.deleteNode(state.selectedNodeId, { repairStrategy });
      console.log('[NetworkEditContext] ✅ Node deleted:', state.selectedNodeId);

      // 3. Actualizar source del mapa
      if (map) {
        const source = map.getSource('canonical-network') as GeoJSONSource;
        if (source) {
          const currentData = (source as any)._data as GeoJSON.FeatureCollection;

          // Filtrar features: remover nodo + edges conectados
          const filteredFeatures = currentData.features.filter(
            (f) =>
              f.properties?.id !== state.selectedNodeId &&
              !connectedEdgeIds.includes(f.properties?.id as string) &&
              !f.properties?.isPreview
          );

          source.setData({
            type: 'FeatureCollection',
            features: filteredFeatures,
          });

          console.log('[NetworkEditContext] ✅ Map source updated');
          console.log('[NetworkEditContext] Removed:', {
            node: state.selectedNodeId,
            edges: connectedEdgeIds.length,
          });
        }
      }

      // 4. Limpiar estado
      setState((prev) => ({
        ...prev,
        previewNode: null,
        selectedNodeId: null,
        editMode: null,
        confirmation: null,
      }));

      // FASE 5.1: Limpiar feature-states después de operación
      cleanupFeatureStates();

      showToast(
        'success',
        `Nodo eliminado con ${getDeletionActionLabel(impact)}`,
        3000,
      );

      console.log('[NetworkEditContext] ✅ Node deletion completed');
    } catch (error: any) {
      console.error('[NetworkEditContext] ❌ Error deleting node:', error);
      showToast(
        'error',
        getNetworkEditErrorMessage(
          error,
          'No se pudo eliminar el nodo. Revisá asociaciones activas o resolvelas manualmente.',
        ),
        6000,
      );
    }
  }, [
    state.selectedNodeId,
    state.confirmation?.metadata?.impact,
    getDeletionActionLabel,
    getNetworkEditErrorMessage,
    showToast,
  ]);

  // ── Acciones: Eliminar Edge (FIX: Hallazgo #4) ────────────────

  /**
   * Muestra confirmación de eliminación de edge
   *
   * EXCLUSIÓN MUTUA: Si geometry edit está activo, se cancela automáticamente.
   */
  const promptDeleteEdge = useCallback(async (edgeId: string, clickCoordinates?: [number, number]) => {
    console.log('[NetworkEditContext] promptDeleteEdge:', edgeId, 'clickCoordinates:', clickCoordinates);

    // Obtener edge desde el source GeoJSON
    const map = mapEngine.getMap();
    if (!map) {
      console.warn('[NetworkEditContext] Map not available');
      return;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // EXCLUSIÓN MUTUA: Cancelar geometry edit si está activo
    // ═══════════════════════════════════════════════════════════════════════════
    if (geometryEditEdgeIdRef.current) {
      console.log('[NetworkEditContext] ⚠️ Cancelling active geometry edit before delete-edge');

      // Limpiar visuales de geometry edit
      const handlesSource = map.getSource('edge-shape-handles') as GeoJSONSource;
      if (handlesSource) {
        handlesSource.setData({ type: 'FeatureCollection', features: [] });
      }
      const previewSource = map.getSource('edge-geometry-preview') as GeoJSONSource;
      if (previewSource) {
        previewSource.setData({ type: 'FeatureCollection', features: [] });
      }
      map.removeFeatureState({ source: 'canonical-network', id: geometryEditEdgeIdRef.current });

      // Limpiar refs
      dragPreviewCoordsRef.current = null;
      geometryEditEdgeIdRef.current = null;
      draggingShapePointIndexRef.current = null;
    }

    const source = map.getSource('canonical-network') as GeoJSONSource;
    if (!source) {
      console.warn('[NetworkEditContext] Source not found');
      return;
    }

    const sourceData = (source as any)._data as GeoJSON.FeatureCollection;
    if (!sourceData || !sourceData.features) {
      console.warn('[NetworkEditContext] Invalid source data');
      return;
    }

    // Buscar edge en el source
    const edgeFeature = sourceData.features.find(
      (f) => f.properties?.id === edgeId && f.properties?.type === 'edge'
    );

    if (!edgeFeature || edgeFeature.geometry.type !== 'LineString') {
      console.warn('[NetworkEditContext] Edge not found:', edgeId);
      return;
    }

    const edgeCoordinates = edgeFeature.geometry.coordinates as [number, number][];
    const midpoint: [number, number] = [
      (edgeCoordinates[0][0] + edgeCoordinates[edgeCoordinates.length - 1][0]) / 2,
      (edgeCoordinates[0][1] + edgeCoordinates[edgeCoordinates.length - 1][1]) / 2,
    ];

    // Usar coordenadas del click si se proporcionaron, si no usar el punto medio del edge
    const confirmationCoordinates = clickCoordinates || midpoint;

    // Obtener impacto de eliminación (asociaciones afectadas)
    let impact: DeletionImpact | undefined;
    try {
      impact = await canonicalNetworkApi.getEdgeDeletionImpact(edgeId);
      console.log('[NetworkEditContext] Edge deletion impact:', impact);
    } catch (error) {
      console.warn('[NetworkEditContext] Could not fetch edge deletion impact:', error);
    }

    // Crear metadata para la confirmación
    const metadata: NetworkEditConfirmationMetadata = {
      message: impact?.canDeleteWithAutoRepair && !impact.canDeleteDirectly
        ? '¿Reparar asociaciones y eliminar segmento?'
        : '¿Eliminar segmento?',
      impact,
    };

    setState((prev) => ({
      ...prev,
      editMode: 'delete-edge',
      selectedEdgeId: edgeId,
      // Crear un edge preview para poder posicionar la confirmación
      previewEdge: {
        id: edgeId,
        geometry: edgeFeature.geometry,
      } as CanonicalEdge,
      // Guardar metadata en confirmation
      confirmation: {
        mode: 'delete-edge',
        position: confirmationCoordinates,
        metadata,
      },
      // Limpiar estado de geometry edit (EXCLUSIÓN MUTUA)
      geometryEditEdgeId: null,
      geometryEditOriginalCoords: null,
      draggingShapePointIndex: null,
    }));

    console.log('[NetworkEditContext] Delete edge prompt shown');
  }, []);

  /**
   * Confirma y elimina edge
   */
  const confirmDeleteEdge = useCallback(async () => {
    console.log('[NetworkEditContext] confirmDeleteEdge');

    // Validar estado
    if (!state.selectedEdgeId) {
      console.error('[NetworkEditContext] Cannot confirm delete: no edge selected');
      return;
    }

    if (!projectId) {
      console.error('[NetworkEditContext] Cannot delete edge: no projectId');
      return;
    }

    try {
      console.log('[NetworkEditContext] ⏳ Deleting edge...');

      const impact = state.confirmation?.metadata?.impact;
      const repairStrategy =
        impact?.canDeleteWithAutoRepair && !impact.canDeleteDirectly ? 'auto' : undefined;

      // 1. Eliminar edge
      await canonicalNetworkApi.deleteEdge(projectId, state.selectedEdgeId, { repairStrategy });
      console.log('[NetworkEditContext] ✅ Edge deleted:', state.selectedEdgeId);

      // 2. Actualizar source del mapa
      const map = mapEngine.getMap();
      if (map) {
        const source = map.getSource('canonical-network') as GeoJSONSource;
        if (source) {
          const currentData = (source as any)._data as GeoJSON.FeatureCollection;

          // Filtrar features: remover edge
          const filteredFeatures = currentData.features.filter(
            (f) => f.properties?.id !== state.selectedEdgeId && !f.properties?.isPreview
          );

          source.setData({
            type: 'FeatureCollection',
            features: filteredFeatures,
          });

          console.log('[NetworkEditContext] ✅ Map source updated, edge removed:', state.selectedEdgeId);
        }
      }

      // 3. Limpiar estado
      setState((prev) => ({
        ...prev,
        previewEdge: null,
        selectedEdgeId: null,
        editMode: null,
        confirmation: null,
      }));

      // FASE 5.1: Limpiar feature-states después de operación
      cleanupFeatureStates();

      showToast(
        'success',
        `Segmento eliminado con ${getDeletionActionLabel(impact)}`,
        3000,
      );

      console.log('[NetworkEditContext] ✅ Edge deletion completed');
    } catch (error: any) {
      console.error('[NetworkEditContext] ❌ Error deleting edge:', error);
      showToast(
        'error',
        getNetworkEditErrorMessage(
          error,
          'No se pudo eliminar el segmento. Revisá asociaciones activas o resolvelas manualmente.',
        ),
        6000,
      );
    }
  }, [
    state.selectedEdgeId,
    state.confirmation?.metadata?.impact,
    projectId,
    getDeletionActionLabel,
    getNetworkEditErrorMessage,
    showToast,
  ]);

  // ── Acciones: Merge Edges ─────────────────────────────────────

  /**
   * Fusiona dos edges contiguos en uno solo
   */
  const mergeEdges = useCallback(async (edgeId1: string, edgeId2: string) => {
    console.log('[NetworkEditContext] mergeEdges:', { edgeId1, edgeId2 });

    if (!projectId) {
      console.error('[NetworkEditContext] Cannot merge edges: no projectId');
      return;
    }

    try {
      console.log('[NetworkEditContext] ⏳ Merging edges...');

      // 1. Ejecutar merge en backend (ejecución inicial)
      // La operación real ocurre AQUÍ, no en el command
      const result = await canonicalNetworkApi.mergeEdges(projectId, edgeId1, edgeId2);
      console.log('[NetworkEditContext] ✅ Edges merged:', result.mergedEdge.id);

      // 2. Crear comando para undo/redo
      // El command guarda el resultado para poder deshacer y rehacer
      const commandData: MergeEdgesCommandData = {
        projectId,
        edgeId1,
        edgeId2,
        mergeResult: result,
      };

      const command = new MergeEdgesCommand(commandData);

      // 3. Registrar comando ya ejecutado
      // IMPORTANTE: usamos registerCommand(), NO executeCommand()
      // - executeCommand() volvería a ejecutar command.execute() → doble merge ❌
      // - registerCommand() solo guarda el command en el stack para undo ✅
      // - command.execute() contiene la lógica de REDO (si se deshace y luego rehace)
      networkUndoRedoService.registerCommand(command);
      console.log('[NetworkEditContext] ✅ MergeEdgesCommand registered for undo');

      // 4. Actualizar store canónico
      console.log('[NetworkEditContext] 📊 Store before merge:', canonicalNetworkStore.getStats());
      console.log('[NetworkEditContext] 🗑️ Removing edges:', { edgeId1, edgeId2, nodeId: result.deletedNodeId });

      // Eliminar edges viejos y nodo compartido
      canonicalNetworkStore.remove([edgeId1, edgeId2, result.deletedNodeId]);

      console.log('[NetworkEditContext] 📊 Store after removal:', canonicalNetworkStore.getStats());

      // Agregar merged edge al store
      const mergedEdgeFeature: CanonicalEdgeFeature = {
        type: 'Feature',
        id: result.mergedEdge.id,
        geometry: result.mergedEdge.geometry,
        properties: {
          id: result.mergedEdge.id,
          type: 'edge',
          fromNodeId: result.mergedEdge.fromNodeId,
          toNodeId: result.mergedEdge.toNodeId,
          lengthMeters: result.mergedEdge.lengthMeters,
          roadClass: result.mergedEdge.roadClass,
        },
      };

      console.log('[NetworkEditContext] ➕ Adding merged edge:', result.mergedEdge.id);
      canonicalNetworkStore.merge([mergedEdgeFeature]);

      console.log('[NetworkEditContext] 📊 Store after adding merged edge:', canonicalNetworkStore.getStats());

      // 5. Sincronizar store → MapLibre source
      canonicalNetworkStore.syncToSource();

      console.log('[NetworkEditContext] ✅ Store and map source updated, edges merged');

      // 6. Limpiar estado
      setState((prev) => ({
        ...prev,
        selectedEdgeId: null,
        edgeContextMenu: null,
      }));

      console.log('[NetworkEditContext] ✅ Merge completed');
    } catch (error: any) {
      console.error('[NetworkEditContext] ❌ Error merging edges:', error);

      // Extraer mensaje detallado del error
      // El backend puede devolver errores específicos sobre validación
      let message = 'Error desconocido al fusionar segmentos';
      if (error?.response?.data?.message) {
        // Error HTTP con mensaje del backend
        message = error.response.data.message;
      } else if (error?.data?.message) {
        // Error con estructura data.message
        message = error.data.message;
      } else if (error?.message) {
        // Error genérico de JavaScript
        message = error.message;
      }

      // Mostrar toast con el error
      showToast('error', `No se pudo fusionar: ${message}`, 6000);
    }
  }, [projectId, showToast]);

  // ── Acciones: Selección de Edges para Merge (Fase 3) ─────────────────────────

  /**
   * Validación local de merge (solo para feedback UX, NO autoritativa)
   *
   * CAPAS DE VALIDACIÓN:
   * 1. Validación local mínima (aquí): Comparten exactamente 1 nodo extremo
   * 2. Validación local ampliada (aquí, opcional): Degree 2 en nodo compartido según store local
   * 3. Validación autoritativa (backend): Atributos, referencias externas, integridad topológica
   *
   * Esta función solo ejecuta validaciones 1 y 2 para dar feedback rápido en UI.
   * El backend siempre tiene la última palabra.
   */
  const validateMergeLocally = useCallback((edgeId1: string, edgeId2: string): { valid: boolean; reason?: string } => {
    // ═══ VALIDACIÓN LOCAL MÍNIMA ═══

    // V1.1: No puede ser el mismo edge
    if (edgeId1 === edgeId2) {
      return { valid: false, reason: 'No se puede fusionar un segmento consigo mismo' };
    }

    // V1.2: Obtener edges del store local
    const edge1 = canonicalNetworkStore.getEdge(edgeId1);
    const edge2 = canonicalNetworkStore.getEdge(edgeId2);

    // DEBUG: Log para diagnosticar problemas de store
    console.log('[NetworkEditContext] validateMergeLocally - store check:', {
      edgeId1,
      edgeId2,
      edge1Exists: !!edge1,
      edge2Exists: !!edge2,
      storeEdgeCount: canonicalNetworkStore.getAllEdges().length,
    });

    if (!edge1 || !edge2) {
      return { valid: false, reason: 'Uno o ambos segmentos no se encuentran en el store local' };
    }

    // V1.3: Deben compartir exactamente 1 nodo extremo
    // NOTE: canonicalNetworkStore.getEdge() retorna CanonicalEdgeFeature (GeoJSON Feature)
    // que tiene properties.fromNodeId y properties.toNodeId (strings), NO objetos fromNode/toNode
    const edge1Nodes = new Set([edge1.properties.fromNodeId, edge1.properties.toNodeId]);
    const edge2Nodes = new Set([edge2.properties.fromNodeId, edge2.properties.toNodeId]);

    const sharedNodes = [...edge1Nodes].filter((nodeId) => edge2Nodes.has(nodeId));

    if (sharedNodes.length === 0) {
      return { valid: false, reason: 'Los segmentos no están conectados' };
    }

    if (sharedNodes.length > 1) {
      return { valid: false, reason: 'Los segmentos comparten más de un nodo (bucle)' };
    }

    // ═══ VALIDACIÓN LOCAL AMPLIADA (OPCIONAL) ═══

    // V2.1: Verificar degree 2 en nodo compartido según store local
    // NOTA: Esta validación usa solo los edges cargados en el store.
    // Si hay edges fuera del viewport, el degree puede ser incorrecto.
    // El backend tiene la validación autoritativa.
    const sharedNodeId = sharedNodes[0];
    const edgesInStore = canonicalNetworkStore.getAllEdges();
    const edgesConnectedToSharedNode = edgesInStore.filter(
      (e) => e.properties.fromNodeId === sharedNodeId || e.properties.toNodeId === sharedNodeId
    );

    if (edgesConnectedToSharedNode.length !== 2) {
      return {
        valid: false,
        reason: `El nodo compartido tiene ${edgesConnectedToSharedNode.length} conexiones en el store (se requieren exactamente 2)`,
      };
    }

    // ═══ TODAS LAS VALIDACIONES LOCALES PASARON ═══
    return { valid: true };
  }, []);

  /**
   * Toggle edge para selección de merge con Shift+click
   *
   * Comportamiento:
   * - Sin Shift: reemplaza selección (solo este edge)
   * - Con Shift + primer edge: agrega a selección (máximo 2)
   * - Con Shift + segundo edge inválido: rechaza sin perder primero
   * - Con Shift + edge ya seleccionado: lo quita de selección
   */
  const toggleEdgeForMerge = useCallback((edgeId: string, shiftKey: boolean) => {
    console.log('[NetworkEditContext] toggleEdgeForMerge:', { edgeId, shiftKey });

    setState((prev) => {
      // Caso 1: Sin Shift → reemplazar selección
      if (!shiftKey) {
        const validation = edgeId ? { valid: true } : null;
        return {
          ...prev,
          selectedEdgesForMerge: edgeId ? [edgeId] : [],
          mergeValidationResult: validation,
        };
      }

      // Caso 2: Con Shift → comportamiento multi-selección
      const currentSelection = prev.selectedEdgesForMerge;

      // Si el edge ya está seleccionado → quitarlo
      if (currentSelection.includes(edgeId)) {
        const newSelection = currentSelection.filter((id) => id !== edgeId);
        return {
          ...prev,
          selectedEdgesForMerge: newSelection,
          mergeValidationResult: newSelection.length === 0 ? null : { valid: true },
        };
      }

      // Si ya hay 2 edges seleccionados → ignorar (no reemplazar)
      if (currentSelection.length >= 2) {
        console.warn('[NetworkEditContext] Ya hay 2 edges seleccionados, ignorando');
        return prev;
      }

      // Agregar edge a selección
      const newSelection = [...currentSelection, edgeId];

      // Si ahora hay 2 edges → validar
      let validation: { valid: boolean; reason?: string } | null = { valid: true };
      if (newSelection.length === 2) {
        validation = validateMergeLocally(newSelection[0], newSelection[1]);

        // Si la validación falla → rechazar el segundo edge y mostrar toast
        if (!validation.valid) {
          console.warn('[NetworkEditContext] Segundo edge inválido para merge:', validation.reason);

          // Mostrar toast con el motivo del rechazo
          showToast('warning', `No se puede fusionar: ${validation.reason}`, 4000);

          // Mantener solo el primer edge, rechazar el segundo
          // NO cambiamos mergeValidationResult para evitar flash visual en edges
          return prev;
        }
      }

      return {
        ...prev,
        selectedEdgesForMerge: newSelection,
        mergeValidationResult: validation,
      };
    });
  }, [validateMergeLocally, showToast]);

  /**
   * Limpia selección de edges para merge
   */
  const clearMergeSelection = useCallback(() => {
    console.log('[NetworkEditContext] clearMergeSelection');
    setState((prev) => ({
      ...prev,
      selectedEdgesForMerge: [],
      mergeValidationResult: null,
    }));
  }, []);

  // ── Acciones: Multi-Merge V1 (Fase 3) ─────────────────────────

  /**
   * Agrega un edge a la selección múltiple con validación en tiempo real
   *
   * FLUJO:
   * 1. PROMOCIÓN: Si hay selectedEdgeId activo, promoverlo a multi-selección
   * 2. Agrega edgeId al array selectedEdgeIds
   * 3. Llama a validateChain backend si hay N ≥ 2 edges
   * 4. Actualiza chainValidation con resultado (isValid, errorMessage, orderedEdgeIds, previewGeometry)
   * 5. Si inválido → muestra toast de error pero mantiene selección
   */
  const addEdgeToSelection = useCallback(async (edgeId: string) => {
    console.log('[NetworkEditContext] addEdgeToSelection:', edgeId);

    if (!projectId) {
      console.error('[NetworkEditContext] Cannot add edge to selection: no projectId');
      return;
    }

    // 1. Calcular selección completa (incluyendo selectedEdgeId si existe)
    let fullSelection = [...state.selectedEdgeIds];

    // PROMOCIÓN: Si hay selectedEdgeId activo (selección simple), promoverlo a multi-selección
    if (state.selectedEdgeId && !fullSelection.includes(state.selectedEdgeId)) {
      console.log('[NetworkEditContext] 🔄 Promoting selectedEdgeId to multi-selection:', state.selectedEdgeId);
      fullSelection.push(state.selectedEdgeId);
    }

    // Agregar nuevo edge (evitar duplicados)
    if (fullSelection.includes(edgeId)) {
      console.log('[NetworkEditContext] Edge already in selection, skipping');
      return;
    }
    fullSelection.push(edgeId);

    console.log('[NetworkEditContext] Full selection after promotion:', fullSelection);

    // 2. Actualizar estado
    setState((prev) => ({
      ...prev,
      selectedEdgeId: null, // Limpiar selección simple al entrar en modo multi
      selectedEdgeIds: fullSelection,
      chainValidation: null, // Reset validation mientras validamos
    }));

    // 3. Validar cadena si hay N ≥ 2 edges
    const currentSelection = fullSelection;

    if (currentSelection.length >= 2) {
      // Helper: Compara si dos selecciones son el mismo conjunto (sin importar orden)
      const isSameSelection = (a: string[], b: string[]): boolean => {
        if (a.length !== b.length) return false;
        const aSorted = [...a].sort().join(',');
        const bSorted = [...b].sort().join(',');
        return aSorted === bSorted;
      };

      try {
        console.log('[NetworkEditContext] ⏳ Validating chain:', currentSelection);

        const result = await canonicalNetworkApi.validateChain(projectId, currentSelection);
        console.log('[NetworkEditContext] ✅ Chain validation result:', result);

        // Actualizar estado con resultado de validación (solo si la selección no cambió)
        setState((prev) => {
          if (!isSameSelection(prev.selectedEdgeIds, currentSelection)) {
            console.log('[NetworkEditContext] ⏭️ Discarding stale validation (selection changed)');
            return prev; // Selección cambió → descartar resultado
          }

          return {
            ...prev,
            chainValidation: {
              isValid: result.isValid,
              errorMessage: result.errorMessage,
              orderedEdgeIds: result.orderedEdgeIds,
              previewGeometry: result.orderedEdgeIds ? null : null, // TODO: calcular preview geometry
            },
          };
        });

        // Si inválido → mostrar toast pero mantener selección
        if (!result.isValid) {
          showToast('warning', `Cadena inválida: ${result.errorMessage}`, 4000);
        } else {
          showToast('success', `Cadena válida: ${result.orderedEdgeIds!.length} segmentos`, 2000);
        }

      } catch (error: any) {
        console.error('[NetworkEditContext] ❌ Error validating chain:', error);

        setState((prev) => {
          if (!isSameSelection(prev.selectedEdgeIds, currentSelection)) {
            console.log('[NetworkEditContext] ⏭️ Discarding stale validation error (selection changed)');
            return prev; // Selección cambió → descartar error
          }

          return {
            ...prev,
            chainValidation: {
              isValid: false,
              errorMessage: error?.message || 'Error al validar cadena',
              orderedEdgeIds: null,
              previewGeometry: null,
            },
          };
        });

        showToast('error', `Error al validar: ${error?.message || 'Error desconocido'}`, 4000);
      }
    }
  }, [projectId, state.selectedEdgeIds, state.selectedEdgeId, showToast]);

  /**
   * Remueve un edge de la selección múltiple
   *
   * FLUJO:
   * 1. Remueve edgeId del array selectedEdgeIds
   * 2. Si quedan N ≥ 2 → re-valida cadena
   * 3. Si quedan N < 2 → limpia chainValidation
   */
  const removeEdgeFromSelection = useCallback(async (edgeId: string) => {
    console.log('[NetworkEditContext] removeEdgeFromSelection:', edgeId);

    if (!projectId) {
      console.error('[NetworkEditContext] Cannot remove edge from selection: no projectId');
      return;
    }

    // 1. Remover edge de selección
    const newSelection = state.selectedEdgeIds.filter(id => id !== edgeId);
    console.log('[NetworkEditContext] New selection after removal:', newSelection);

    setState((prev) => ({
      ...prev,
      selectedEdgeIds: newSelection,
      chainValidation: null, // Reset validation
    }));

    // 2. Re-validar si hay N ≥ 2
    if (newSelection.length >= 2) {
      try {
        console.log('[NetworkEditContext] ⏳ Re-validating chain:', newSelection);

        const result = await canonicalNetworkApi.validateChain(projectId, newSelection);
        console.log('[NetworkEditContext] ✅ Chain validation result:', result);

        setState((prev) => ({
          ...prev,
          chainValidation: {
            isValid: result.isValid,
            errorMessage: result.errorMessage,
            orderedEdgeIds: result.orderedEdgeIds,
            previewGeometry: null, // TODO: calcular preview geometry
          },
        }));

        if (!result.isValid) {
          showToast('warning', `Cadena inválida: ${result.errorMessage}`, 4000);
        }

      } catch (error: any) {
        console.error('[NetworkEditContext] ❌ Error re-validating chain:', error);

        setState((prev) => ({
          ...prev,
          chainValidation: {
            isValid: false,
            errorMessage: error?.message || 'Error al validar cadena',
            orderedEdgeIds: null,
            previewGeometry: null,
          },
        }));
      }
    }
  }, [projectId, state.selectedEdgeIds, showToast]);

  /**
   * Limpia la selección múltiple completamente
   */
  const clearSelection = useCallback(() => {
    console.log('[NetworkEditContext] clearSelection');
    setState((prev) => ({
      ...prev,
      selectedEdgeIds: [],
      chainValidation: null,
    }));
  }, []);

  /**
   * Confirma y ejecuta el multi-merge de todos los edges seleccionados
   *
   * PRE-CONDICIONES:
   * - selectedEdgeIds.length ≥ 2
   * - chainValidation?.isValid === true
   *
   * FLUJO:
   * 1. Valida pre-condiciones
   * 2. Ejecuta mergeMultipleEdges en backend (ejecución inicial)
   * 3. Actualiza store canónico (remueve edges/nodos viejos, agrega merged edge)
   * 4. Registra MergeMultipleEdgesCommand para undo/redo (TODO: Fase 4)
   * 5. Limpia selección
   * 6. Selecciona merged edge
   */
  const confirmMultiMerge = useCallback(async () => {
    console.log('[NetworkEditContext] confirmMultiMerge');

    if (!projectId) {
      console.error('[NetworkEditContext] Cannot confirm multi-merge: no projectId');
      return;
    }

    // 1. Validar pre-condiciones
    if (state.selectedEdgeIds.length < 2) {
      console.error('[NetworkEditContext] Cannot merge: need at least 2 edges');
      showToast('error', 'Selecciona al menos 2 segmentos para fusionar', 4000);
      return;
    }

    if (!state.chainValidation?.isValid) {
      console.error('[NetworkEditContext] Cannot merge: chain validation failed');
      showToast('error', `No se puede fusionar: ${state.chainValidation?.errorMessage || 'Cadena inválida'}`, 4000);
      return;
    }

    try {
      console.log('[NetworkEditContext] ⏳ Merging multiple edges...', state.chainValidation.orderedEdgeIds);

      // 2. Ejecutar merge en backend (usar orderedEdgeIds del resultado de validación)
      const result = await canonicalNetworkApi.mergeMultipleEdges(
        projectId,
        state.chainValidation.orderedEdgeIds!
      );
      console.log('[NetworkEditContext] ✅ Edges merged:', result.mergedEdge.id);

      // TODO Fase 4: Registrar comando para undo/redo
      // const command = new MergeMultipleEdgesCommand({ projectId, result });
      // networkUndoRedoService.registerCommand(command);

      // 3. Actualizar store canónico
      console.log('[NetworkEditContext] 📊 Store before merge:', canonicalNetworkStore.getStats());
      console.log('[NetworkEditContext] 🗑️ Removing edges and nodes:', {
        edges: result.deletedEdgeIds,
        nodes: result.deletedNodeIds,
      });

      // Remover edges y nodos viejos
      canonicalNetworkStore.remove([...result.deletedEdgeIds, ...result.deletedNodeIds]);

      console.log('[NetworkEditContext] 📊 Store after removal:', canonicalNetworkStore.getStats());

      // Agregar merged edge
      const mergedEdgeFeature: CanonicalEdgeFeature = {
        type: 'Feature',
        id: result.mergedEdge.id,
        geometry: result.mergedEdge.geometry,
        properties: {
          id: result.mergedEdge.id,
          type: 'edge',
          fromNodeId: result.mergedEdge.fromNodeId,
          toNodeId: result.mergedEdge.toNodeId,
          lengthMeters: result.mergedEdge.lengthMeters,
          roadClass: result.mergedEdge.roadClass,
        },
      };

      console.log('[NetworkEditContext] ➕ Adding merged edge:', result.mergedEdge.id);
      canonicalNetworkStore.merge([mergedEdgeFeature]);

      console.log('[NetworkEditContext] 📊 Store after adding merged edge:', canonicalNetworkStore.getStats());

      // 4. Sincronizar store → MapLibre source
      canonicalNetworkStore.syncToSource();

      console.log('[NetworkEditContext] ✅ Store and map source updated, edges merged');

      // 5. Limpiar selección
      setState((prev) => ({
        ...prev,
        selectedEdgeIds: [],
        chainValidation: null,
        selectedEdgeId: result.mergedEdge.id, // Seleccionar merged edge
        edgeContextMenu: null,
      }));

      showToast('success', `${result.deletedEdgeIds.length} segmentos fusionados correctamente`, 3000);

      console.log('[NetworkEditContext] ✅ Multi-merge completed');
    } catch (error: any) {
      console.error('[NetworkEditContext] ❌ Error merging multiple edges:', error);

      // Extraer mensaje detallado del error
      let message = 'Error desconocido al fusionar segmentos';
      if (error?.response?.data?.message) {
        message = error.response.data.message;
      } else if (error?.data?.message) {
        message = error.data.message;
      } else if (error?.message) {
        message = error.message;
      }

      showToast('error', `No se pudo fusionar: ${message}`, 6000);
    }
  }, [projectId, state.selectedEdgeIds, state.chainValidation, showToast]);

  // ── Acciones: Draw Freehand Segment (Etapa 1) ─────────────────

  /**
   * Inicia dibujo de segmento a mano alzada desde nodo
   */
  const startFreehandSegmentDrawing = useCallback((nodeId: string) => {
    console.log('[NetworkEditContext] startFreehandSegmentDrawing:', nodeId);

    if (!nodeId) {
      console.warn('[NetworkEditContext] No nodeId provided');
      return;
    }

    // Buscar coordenadas del nodo origen en el store
    const nodeFeature = canonicalNetworkStore.getNode(nodeId);
    if (!nodeFeature) {
      console.error('[NetworkEditContext] Node not found in store:', nodeId);
      showToast('error', 'Nodo no encontrado', 2000);
      return;
    }

    const nodeCoords = nodeFeature.geometry.coordinates as [number, number];

    console.log('[NetworkEditContext] 🎨 Starting freehand segment drawing from node:', {
      nodeId,
      coords: nodeCoords,
    });

    setState((prev) => ({
      ...prev,
      isDrawingFreehandSegment: true,
      freehandSourceNodeId: nodeId,
      freehandSourceCoords: nodeCoords,
      freehandIntermediatePoints: [],
      freehandPreviewEdge: null,
      editMode: 'draw-freehand-segment',
      selectedNodeId: nodeId,
    }));

    showToast('info', 'Click para agregar puntos, ESC para cancelar', 3000);
  }, [showToast]);

  /**
   * Agrega shape point intermedio (NO crea nodo topológico)
   */
  const addFreehandShapePoint = useCallback((coords: [number, number]) => {
    console.log('[NetworkEditContext] addFreehandShapePoint:', coords);

    setState((prev) => {
      if (!prev.isDrawingFreehandSegment) {
        console.warn('[NetworkEditContext] Not in freehand drawing mode');
        return prev;
      }

      const newIntermediatePoints = [...prev.freehandIntermediatePoints, coords];

      console.log('[NetworkEditContext] 📍 Shape point added:', {
        index: newIntermediatePoints.length,
        coords,
        totalPoints: newIntermediatePoints.length,
      });

      return {
        ...prev,
        freehandIntermediatePoints: newIntermediatePoints,
      };
    });

    // Feedback visual mínimo (opcional en Etapa 1)
    // showToast('info', `Punto ${state.freehandIntermediatePoints.length + 1}`, 500);
  }, []);

  /**
   * Actualiza preview durante mouse move
   */
  const updateFreehandPreview = useCallback((cursorCoords: [number, number]) => {
    // PERF: No logear cada mousemove para evitar flood
    // console.log('[NetworkEditContext] updateFreehandPreview:', cursorCoords);

    setState((prev) => {
      if (!prev.isDrawingFreehandSegment || !prev.freehandSourceCoords) {
        return prev;
      }

      // Construir geometría acumulativa: source + intermediates + cursor
      const previewCoords: Array<[number, number]> = [
        prev.freehandSourceCoords,
        ...prev.freehandIntermediatePoints,
        cursorCoords,
      ];

      const previewEdge: CanonicalEdge = {
        id: 'freehand-preview-edge',
        geometry: {
          type: 'LineString',
          coordinates: previewCoords,
        },
      } as CanonicalEdge;

      return {
        ...prev,
        freehandPreviewEdge: previewEdge,
      };
    });
  }, []);

  /**
   * Cancela dibujo de segmento a mano alzada
   */
  const cancelFreehandSegmentDrawing = useCallback(() => {
    console.log('[NetworkEditContext] cancelFreehandSegmentDrawing');

    setState((prev) => ({
      ...prev,
      isDrawingFreehandSegment: false,
      freehandSourceNodeId: null,
      freehandSourceCoords: null,
      freehandIntermediatePoints: [],
      freehandPreviewEdge: null,
      editMode: null,
    }));

    console.log('[NetworkEditContext] ✅ Freehand segment drawing cancelled');
    showToast('info', 'Dibujo cancelado', 2000);
  }, [showToast]);

  /**
   * Confirma y persiste segmento a mano alzada con geometría multipunto
   * Etapa 2: Terminación y Persistencia
   */
  const confirmFreehandSegment = useCallback(async (intent: BranchIntent): Promise<void> => {
    console.log('[NetworkEditContext] confirmFreehandSegment:', intent);

    if (!state.isDrawingFreehandSegment || !state.freehandSourceNodeId || !state.freehandSourceCoords) {
      console.error('[NetworkEditContext] Invalid freehand state for confirmation');
      showToast('error', 'Estado de dibujo inválido', 2000);
      return;
    }

    if (!projectId) {
      console.error('[NetworkEditContext] No project selected');
      showToast('error', 'No hay proyecto seleccionado', 2000);
      return;
    }

    // Construir geometría final: source + intermediates + target
    let finalCoords: Array<[number, number]>;
    let targetNodeId: string | undefined;

    if (intent.type === 'snap-node') {
      // Caso A: Finalizar en nodo existente
      if (!intent.nodeId || !intent.coords) {
        console.error('[NetworkEditContext] snap-node intent requires nodeId and coords');
        showToast('error', 'Snap inválido', 2000);
        return;
      }
      targetNodeId = intent.nodeId;
      finalCoords = [
        state.freehandSourceCoords,
        ...state.freehandIntermediatePoints,
        intent.coords, // Usar coordenadas exactas del snap
      ];
      console.log('[NetworkEditContext] 🎯 Freehand finalization: snap-node', {
        sourceNodeId: state.freehandSourceNodeId,
        targetNodeId,
        intermediatePoints: state.freehandIntermediatePoints.length,
        totalCoords: finalCoords.length,
      });
    } else if (intent.type === 'free-point') {
      // Caso B: Finalizar en punto libre (crear nuevo nodo terminal)
      if (!intent.coords) {
        console.error('[NetworkEditContext] free-point intent requires coords');
        showToast('error', 'Coordenadas inválidas', 2000);
        return;
      }
      targetNodeId = undefined; // Backend creará nuevo nodo
      finalCoords = [
        state.freehandSourceCoords,
        ...state.freehandIntermediatePoints,
        intent.coords,
      ];
      console.log('[NetworkEditContext] 🎯 Freehand finalization: free-point (new node)', {
        sourceNodeId: state.freehandSourceNodeId,
        intermediatePoints: state.freehandIntermediatePoints.length,
        totalCoords: finalCoords.length,
      });
    } else {
      console.error('[NetworkEditContext] Unsupported intent type for freehand:', intent.type);
      showToast('error', 'Tipo de finalización no soportado', 2000);
      return;
    }

    // Validar geometría mínima (al menos 2 puntos)
    if (finalCoords.length < 2) {
      console.error('[NetworkEditContext] Insufficient coordinates for edge:', finalCoords.length);
      showToast('error', 'Geometría insuficiente', 2000);
      return;
    }

    try {
      // Llamar a API para crear rama con geometría multipunto
      const result = await canonicalNetworkApi.createBranch({
        projectId: projectId,
        sourceNodeId: state.freehandSourceNodeId,
        targetNodeId, // undefined si es free-point (backend crea nodo nuevo)
        geometry: {
          type: 'LineString',
          coordinates: finalCoords,
        },
        intent: intent.type,
      });

      console.log('[NetworkEditContext] ✅ Freehand segment created:', result);

      // Actualizar store canónico con los nuevos elementos
      if (result.newEdge) {
        const edgeFeature = rawToFeature(result.newEdge, 'edge') as CanonicalEdgeFeature;
        canonicalNetworkStore.addEdge(edgeFeature);
      }

      if (result.newNode) {
        const nodeFeature = rawToFeature(result.newNode, 'node') as CanonicalNodeFeature;
        canonicalNetworkStore.addNode(nodeFeature);
      }

      // Registrar comando para undo/redo
      const command = new CreateBranchCommand(state.freehandSourceNodeId, intent, result);
      await networkUndoRedoService.executeCommand(command);

      // Limpiar estado freehand
      setState((prev) => ({
        ...prev,
        isDrawingFreehandSegment: false,
        freehandSourceNodeId: null,
        freehandSourceCoords: null,
        freehandIntermediatePoints: [],
        freehandPreviewEdge: null,
        editMode: null,
        selectedEdgeId: result.newEdge?.id || null,
      }));

      showToast('success', 'Segmento creado con éxito', 2000);

    } catch (error) {
      console.error('[NetworkEditContext] ❌ Error creating freehand segment:', error);
      showToast('error', 'Error al crear segmento', 3000);
    }
  }, [state.isDrawingFreehandSegment, state.freehandSourceNodeId, state.freehandSourceCoords, state.freehandIntermediatePoints, projectId, showToast]);

  /**
   * Handler de ESC para cancelar dibujo freehand
   */
  useEffect(() => {
    if (!state.isDrawingFreehandSegment) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelFreehandSegmentDrawing();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [state.isDrawingFreehandSegment, cancelFreehandSegmentDrawing]);

  // ── Acciones: Menú Contextual de Edge ─────────────────────────

  /**
   * Abre menú contextual para edge
   */
  const openEdgeContextMenu = useCallback((edgeId: string, position: [number, number]) => {
    console.log('[NetworkEditContext] openEdgeContextMenu:', edgeId, position);
    setState((prev) => ({
      ...prev,
      edgeContextMenu: { edgeId, position },
      selectedEdgeId: edgeId,
    }));
  }, []);

  /**
   * Cierra menú contextual
   */
  const closeEdgeContextMenu = useCallback(() => {
    console.log('[NetworkEditContext] closeEdgeContextMenu');
    setState((prev) => ({
      ...prev,
      edgeContextMenu: null,
    }));
  }, []);

  // ── Acciones: Menú Contextual de Nodo ─────────────────────────

  /**
   * Abre menú contextual para nodo
   */
  const openNodeContextMenu = useCallback((nodeId: string, position: [number, number]) => {
    console.log('[NetworkEditContext] openNodeContextMenu:', nodeId, position);
    setState((prev) => ({
      ...prev,
      nodeContextMenu: { nodeId, position },
      selectedNodeId: nodeId,
    }));
  }, []);

  /**
   * Cierra menú contextual de nodo
   */
  const closeNodeContextMenu = useCallback(() => {
    console.log('[NetworkEditContext] closeNodeContextMenu');
    setState((prev) => ({
      ...prev,
      nodeContextMenu: null,
    }));
  }, []);

  // ── Acciones: Insertar Nodo en Edge ─────────────────────────

  /**
   * Inicia modo inserción de nodo sobre edge (ghost node sigue mouse)
   *
   * EXCLUSIÓN MUTUA: Si geometry edit está activo, se cancela automáticamente.
   */
  const startInsertNodeOnEdge = useCallback((edgeId: string) => {
    console.log('[NetworkEditContext] startInsertNodeOnEdge:', edgeId);

    const map = mapEngine.getMap();

    // ═══════════════════════════════════════════════════════════════════════════
    // EXCLUSIÓN MUTUA: Cancelar geometry edit si está activo
    // ═══════════════════════════════════════════════════════════════════════════
    if (geometryEditEdgeIdRef.current) {
      console.log('[NetworkEditContext] ⚠️ Cancelling active geometry edit before starting insert-node-on-edge');

      // Limpiar visuales de geometry edit
      if (map) {
        // Limpiar handles source
        const handlesSource = map.getSource('edge-shape-handles') as GeoJSONSource;
        if (handlesSource) {
          handlesSource.setData({ type: 'FeatureCollection', features: [] });
        }
        // Limpiar preview source
        const previewSource = map.getSource('edge-geometry-preview') as GeoJSONSource;
        if (previewSource) {
          previewSource.setData({ type: 'FeatureCollection', features: [] });
        }
        // Restaurar visibilidad del edge
        map.removeFeatureState({ source: 'canonical-network', id: geometryEditEdgeIdRef.current });
      }

      // Limpiar refs
      dragPreviewCoordsRef.current = null;
      geometryEditEdgeIdRef.current = null;
      draggingShapePointIndexRef.current = null;
    }

    setState((prev) => ({
      ...prev,
      editMode: 'insert-node-on-edge',
      insertNodeTargetEdgeId: edgeId,
      selectedEdgeId: edgeId,
      edgeContextMenu: null, // Cerrar menú contextual
      previewNode: null, // Se creará en updateInsertNodePosition
      previewEdge: null,
      previewEdge2: null,
      // Limpiar estado de geometry edit (EXCLUSIÓN MUTUA)
      geometryEditEdgeId: null,
      geometryEditOriginalCoords: null,
      draggingShapePointIndex: null,
    }));

    // Cambiar cursor del mapa
    if (map) {
      map.getCanvas().style.cursor = 'crosshair';
    }
  }, []);

  /**
   * Actualiza posición del ghost node durante inserción
   * El nodo se proyecta sobre el edge y preserva la geometría original
   */
  const updateInsertNodePosition = useCallback((cursorPosition: [number, number]) => {
    if (state.editMode !== 'insert-node-on-edge' || !state.insertNodeTargetEdgeId) {
      return;
    }

    const map = mapEngine.getMap();
    if (!map) return;

    const source = map.getSource('canonical-network') as GeoJSONSource;
    if (!source) return;

    const sourceData = (source as any)._data as GeoJSON.FeatureCollection;
    if (!sourceData || !sourceData.features) return;

    // Buscar edge target
    const edgeFeature = sourceData.features.find(
      (f) => f.properties?.id === state.insertNodeTargetEdgeId && f.properties?.type === 'edge'
    );

    if (!edgeFeature || edgeFeature.geometry.type !== 'LineString') {
      return;
    }

    const edgeCoordinates = edgeFeature.geometry.coordinates as [number, number][];

    // Proyectar cursor sobre el edge - encontrar el segmento más cercano
    const cursorScreen = map.project(cursorPosition);
    let minDist = Infinity;
    let closestPoint: [number, number] | null = null;
    let closestSegmentIndex = 0; // Índice del segmento donde cae el punto

    for (let i = 0; i < edgeCoordinates.length - 1; i++) {
      const p1 = map.project(edgeCoordinates[i]);
      const p2 = map.project(edgeCoordinates[i + 1]);

      // Calcular punto más cercano en el segmento
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;

      if (dx === 0 && dy === 0) continue;

      const t = Math.max(0, Math.min(1,
        ((cursorScreen.x - p1.x) * dx + (cursorScreen.y - p1.y) * dy) / (dx * dx + dy * dy)
      ));

      const closestScreenX = p1.x + t * dx;
      const closestScreenY = p1.y + t * dy;
      const dist = Math.sqrt(
        Math.pow(closestScreenX - cursorScreen.x, 2) +
        Math.pow(closestScreenY - cursorScreen.y, 2)
      );

      if (dist < minDist) {
        minDist = dist;
        const geoPoint = map.unproject([closestScreenX, closestScreenY]);
        closestPoint = [geoPoint.lng, geoPoint.lat];
        closestSegmentIndex = i;
      }
    }

    if (!closestPoint) return;

    // Crear preview node
    const previewNode: Partial<CanonicalNode> = {
      id: 'preview-insert-node',
      geometry: { type: 'Point', coordinates: closestPoint },
    };

    // Crear preview edges PRESERVANDO la geometría original
    // Edge1: desde el inicio hasta el punto de inserción (incluyendo puntos intermedios)
    const edge1Coords: [number, number][] = [
      ...edgeCoordinates.slice(0, closestSegmentIndex + 1),
      closestPoint,
    ];

    // Edge2: desde el punto de inserción hasta el final (incluyendo puntos intermedios)
    const edge2Coords: [number, number][] = [
      closestPoint,
      ...edgeCoordinates.slice(closestSegmentIndex + 1),
    ];

    const previewEdge1: Partial<CanonicalEdge> = {
      id: 'preview-insert-edge-1',
      geometry: { type: 'LineString', coordinates: edge1Coords },
    };

    const previewEdge2: Partial<CanonicalEdge> = {
      id: 'preview-insert-edge-2',
      geometry: { type: 'LineString', coordinates: edge2Coords },
    };

    setState((prev) => ({
      ...prev,
      previewNode: previewNode as CanonicalNode,
      previewEdge: previewEdge1 as CanonicalEdge,
      previewEdge2: previewEdge2 as CanonicalEdge,
    }));
  }, [state.editMode, state.insertNodeTargetEdgeId]);

  /**
   * Confirma inserción de nodo dividiendo el edge
   */
  const confirmInsertNode = useCallback(async () => {
    console.log('[NetworkEditContext] confirmInsertNode');

    if (!state.previewNode || !state.previewEdge || !state.previewEdge2 || !state.insertNodeTargetEdgeId || !projectId) {
      console.error('[NetworkEditContext] Cannot confirm insert: missing required data');
      return;
    }

    // CRITICAL: Capturar datos del preview ANTES de cambiar estado
    // Esto evita que el ghost node siga moviéndose mientras se ejecutan las API calls
    const previewNodeGeometry = state.previewNode.geometry;
    const previewEdgeGeometry = state.previewEdge.geometry;
    const previewEdge2Geometry = state.previewEdge2.geometry;
    const targetEdgeId = state.insertNodeTargetEdgeId;

    // Inmediatamente salir del modo de edición para congelar el preview
    setState((prev) => ({
      ...prev,
      editMode: null,
    }));

    // Cambiar cursor a espera
    const map = mapEngine.getMap();
    if (map) {
      map.getCanvas().style.cursor = 'wait';
    }

    // Variables para tracking de recursos creados (para rollback)
    let insertedNode: CanonicalNode | null = null;
    let newEdge1: CanonicalEdge | null = null;
    let newEdge2: CanonicalEdge | null = null;

    try {
      console.log('[NetworkEditContext] ⏳ Inserting node...');

      // 1. Obtener edge original (usando valor capturado)
      const originalEdge = await canonicalNetworkApi.getEdge(targetEdgeId);
      console.log('[NetworkEditContext] Original edge:', originalEdge);

      // 2. Crear nodo en el punto de inserción (usando geometría capturada)
      insertedNode = await canonicalNetworkApi.createNode(projectId, {
        geometry: previewNodeGeometry,
        sourceType: 'manual',
        isManualAuthority: true,
      });

      console.log('[NetworkEditContext] ✅ Inserted node created:', insertedNode.id);

      // FIX: Validar roadClass antes de crear edges
      const VALID_ROAD_CLASSES = new Set([
        'motorway', 'trunk', 'primary', 'secondary', 'tertiary',
        'residential', 'service', 'unclassified', 'pedestrian',
        'cycleway', 'footway'
      ]);

      const validRoadClass =
        originalEdge.roadClass &&
        VALID_ROAD_CLASSES.has(originalEdge.roadClass)
          ? originalEdge.roadClass
          : 'residential';

      console.log('[NetworkEditContext] 📋 Original edge roadClass:', {
        original: originalEdge.roadClass,
        normalized: validRoadClass
      });

      // 3. Crear primer edge (fromNode → insertedNode) (usando geometría capturada)
      newEdge1 = await canonicalNetworkApi.createEdge(projectId, {
        fromNodeId: originalEdge.fromNode.id,
        toNodeId: insertedNode.id,
        geometry: previewEdgeGeometry,
        sourceType: 'manual',
        roadClass: validRoadClass,
        accessMode: originalEdge.accessMode ?? undefined,
        onewayDirection: originalEdge.onewayDirection ?? undefined,
        surfaceType: originalEdge.surfaceType ?? undefined,
      });

      console.log('[NetworkEditContext] ✅ First edge created:', newEdge1.id);

      // 4. Crear segundo edge (insertedNode → toNode) (usando geometría capturada)
      newEdge2 = await canonicalNetworkApi.createEdge(projectId, {
        fromNodeId: insertedNode.id,
        toNodeId: originalEdge.toNode.id,
        geometry: previewEdge2Geometry,
        sourceType: 'manual',
        roadClass: validRoadClass,
        accessMode: originalEdge.accessMode ?? undefined,
        onewayDirection: originalEdge.onewayDirection ?? undefined,
        surfaceType: originalEdge.surfaceType ?? undefined,
      });

      console.log('[NetworkEditContext] ✅ Second edge created:', newEdge2.id);

      // 5. Eliminar edge original (usando ID capturado)
      await canonicalNetworkApi.deleteEdge(projectId, targetEdgeId);
      console.log('[NetworkEditContext] ✅ Original edge deleted');

      // ═══════════════════════════════════════════════════════════════════════════
      // 6. CRÍTICO: Actualizar canonical store ANTES del map source
      // Esto garantiza consistencia entre store y visualización
      // ═══════════════════════════════════════════════════════════════════════════

      // 6.1 Crear features para el store
      const newNodeFeature: CanonicalNodeFeature = {
        type: 'Feature',
        id: insertedNode.id,
        geometry: insertedNode.geometry as GeoJSON.Point,
        properties: {
          id: insertedNode.id,
          type: 'node',
          intersectionType: 'simple',
        },
      };

      const newEdge1Feature: CanonicalEdgeFeature = {
        type: 'Feature',
        id: newEdge1.id,
        geometry: newEdge1.geometry as GeoJSON.LineString,
        properties: {
          id: newEdge1.id,
          type: 'edge',
          fromNodeId: newEdge1.fromNode.id,
          toNodeId: newEdge1.toNode.id,
          roadClass: newEdge1.roadClass || null,
          lengthMeters: newEdge1.lengthMeters || null,
        },
      };

      const newEdge2Feature: CanonicalEdgeFeature = {
        type: 'Feature',
        id: newEdge2.id,
        geometry: newEdge2.geometry as GeoJSON.LineString,
        properties: {
          id: newEdge2.id,
          type: 'edge',
          fromNodeId: newEdge2.fromNode.id,
          toNodeId: newEdge2.toNode.id,
          roadClass: newEdge2.roadClass || null,
          lengthMeters: newEdge2.lengthMeters || null,
        },
      };

      // 6.2 Remover edge original del store
      canonicalNetworkStore.remove([targetEdgeId]);
      console.log('[NetworkEditContext] ✅ Original edge removed from store');

      // 6.3 Agregar nuevos features al store y sincronizar con source
      canonicalNetworkStore.updateAndSync([newNodeFeature, newEdge1Feature, newEdge2Feature]);
      console.log('[NetworkEditContext] ✅ New features added to store and synced');

      // ═══════════════════════════════════════════════════════════════════════════
      // 6.4 UNDO: Registrar comando para deshacer la operación
      // ═══════════════════════════════════════════════════════════════════════════
      const undoCommand = new InsertNodeOnEdgeCommand({
        projectId,
        originalEdgeId: targetEdgeId,
        originalEdgeGeometry: originalEdge.geometry as { type: 'LineString'; coordinates: [number, number][] },
        originalFromNodeId: originalEdge.fromNode.id,
        originalToNodeId: originalEdge.toNode.id,
        originalRoadClass: originalEdge.roadClass || null,
        insertedNodeId: insertedNode.id,
        insertedNodeGeometry: insertedNode.geometry as { type: 'Point'; coordinates: [number, number] },
        edge1Id: newEdge1.id,
        edge1Geometry: newEdge1.geometry as { type: 'LineString'; coordinates: [number, number][] },
        edge2Id: newEdge2.id,
        edge2Geometry: newEdge2.geometry as { type: 'LineString'; coordinates: [number, number][] },
      });
      networkUndoRedoService.registerCommand(undoCommand);
      console.log('[NetworkEditContext] ✅ Undo command registered for insert-node-on-edge');

      // 7. Limpiar estado restante (incluyendo geometry edit para evitar estado stuck)
      setState((prev) => ({
        ...prev,
        insertNodeTargetEdgeId: null,
        previewNode: null,
        previewEdge: null,
        previewEdge2: null,
        selectedEdgeId: null,
        // CRÍTICO: Limpiar geometry edit para evitar "Already editing edge"
        geometryEditEdgeId: null,
        geometryEditOriginalCoords: null,
        draggingShapePointIndex: null,
      }));

      // Limpiar ref de geometry edit también
      geometryEditEdgeIdRef.current = null;

      // Restaurar cursor
      if (map) {
        map.getCanvas().style.cursor = '';
      }

      cleanupFeatureStates();
      console.log('[NetworkEditContext] ✅ Node insertion completed');
    } catch (error) {
      console.error('[NetworkEditContext] ❌ Error inserting node:', error);

      // ═══════════════════════════════════════════════════════════════════════════
      // ROLLBACK: Eliminar recursos creados en orden inverso para mantener consistencia
      // Orden: edge2 → edge1 → node (debemos eliminar edges antes que el nodo)
      // ═══════════════════════════════════════════════════════════════════════════
      console.log('[NetworkEditContext] 🔄 Starting rollback...');

      // Rollback edge2
      if (newEdge2?.id) {
        try {
          await canonicalNetworkApi.deleteEdge(projectId, newEdge2.id);
          console.log('[NetworkEditContext] 🔄 ROLLBACK: Edge2 deleted:', newEdge2.id);
        } catch (rollbackError) {
          console.error('[NetworkEditContext] ⚠️ Rollback failed for edge2:', rollbackError);
        }
      }

      // Rollback edge1
      if (newEdge1?.id) {
        try {
          await canonicalNetworkApi.deleteEdge(projectId, newEdge1.id);
          console.log('[NetworkEditContext] 🔄 ROLLBACK: Edge1 deleted:', newEdge1.id);
        } catch (rollbackError) {
          console.error('[NetworkEditContext] ⚠️ Rollback failed for edge1:', rollbackError);
        }
      }

      // Rollback node (solo después de eliminar edges)
      if (insertedNode?.id) {
        try {
          await canonicalNetworkApi.deleteNode(insertedNode.id);
          console.log('[NetworkEditContext] 🔄 ROLLBACK: Node deleted:', insertedNode.id);
        } catch (rollbackError) {
          console.error('[NetworkEditContext] ⚠️ Rollback failed for node:', rollbackError);
        }
      }

      console.log('[NetworkEditContext] 🔄 Rollback completed');

      // Restaurar estado de edición para que el usuario pueda reintentar
      setState((prev) => ({
        ...prev,
        editMode: 'insert-node-on-edge',
        insertNodeTargetEdgeId: targetEdgeId,
      }));

      // En caso de error, restaurar cursor
      if (map) {
        map.getCanvas().style.cursor = 'crosshair';
      }

      alert('Error al insertar nodo. La operación fue revertida. Intente nuevamente.');
    }
  }, [state.previewNode, state.previewEdge, state.previewEdge2, state.insertNodeTargetEdgeId, projectId]);

  // ── Acciones: Mover Nodo ────────────────────────────────────

  /**
   * Inicia movimiento de nodo (drag start)
   */
  const startNodeMove = useCallback((node: CanonicalNode, connectedEdges: CanonicalEdge[]) => {
    console.log('[NetworkEditContext] startNodeMove:', node.id, connectedEdges.length, 'edges');

    const position = node.geometry.coordinates as [number, number];

    // Generar preview inicial con la posición actual del nodo
    const initialPreview = buildPreviewForMovedNode(node, position, connectedEdges);

    setState((prev) => ({
      ...prev,
      editMode: 'move-node',
      selectedNodeId: node.id,
      originalNodePosition: position,
      connectedEdgeIds: connectedEdges.map((e) => e.id),
      connectedEdges,
      movingNode: node,
      isDragging: true,
      // Establecer preview inicial (posición actual sin cambios)
      previewNode: initialPreview.node,
      previewEdges: initialPreview.affectedEdges,
    }));
  }, []);

  /**
   * Actualiza posición del nodo mientras se arrastra
   *
   * @remarks
   * Usa funciones puras de topology.service para recalcular geometrías.
   * No consulta el source del mapa en cada mousemove (performance).
   */
  const updateNodePosition = useCallback((newPosition: [number, number]) => {
    if (state.editMode !== 'move-node' || !state.movingNode || !state.connectedEdges) {
      return;
    }

    // Usar función pura para calcular preview
    const preview = buildPreviewForMovedNode(
      state.movingNode,
      newPosition,
      state.connectedEdges
    );

    // Actualizar estado con preview calculado
    setState((prev) => ({
      ...prev,
      previewNode: preview.node,
      previewEdges: preview.affectedEdges,
      // Mantener compatibilidad con otros modos
      previewEdge: preview.affectedEdges[0] || null,
      previewEdge2: preview.affectedEdges[1] || null,
    }));
  }, [state.editMode, state.movingNode, state.connectedEdges]);

  /**
   * Confirma nueva posición del nodo
   *
   * BUG 1 FIX: Preserva shape points intermedios de edges
   * BUG 2 FIX: Registra MoveNodeCommand para undo/redo
   */
  const confirmNodeMove = useCallback(async () => {
    console.log('[NetworkEditContext] confirmNodeMove');
    console.log('[DIAG:MOVE] ═══════════════════════════════════════');

    // Validar estado
    if (!state.selectedNodeId || !state.previewNode || !projectId) {
      console.error('[NetworkEditContext] Cannot confirm move: missing data');
      return;
    }

    // BUG 2 FIX: Usar state.connectedEdges (original) y state.movingNode para beforeState
    if (!state.movingNode || !state.connectedEdges) {
      console.error('[NetworkEditContext] Cannot confirm move: missing movingNode or connectedEdges');
      return;
    }

    // ═══════════════════════════════════════════════════════════════════════
    // BUG 2 FIX: Capturar beforeState ANTES de cualquier modificación
    // ═══════════════════════════════════════════════════════════════════════
    const beforeNodeFeature: CanonicalNodeFeature = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: state.originalNodePosition || state.movingNode.geometry.coordinates,
      },
      properties: {
        id: state.movingNode.id,
        type: 'node',
        intersectionType: state.movingNode.intersectionType,
        nodeVersion: (state.movingNode as any).nodeVersion || 1,
      },
    };

    const beforeEdgeFeatures: CanonicalEdgeFeature[] = state.connectedEdges.map((edge) => ({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        // CRÍTICO: Clonar profundamente para evitar mutación
        coordinates: edge.geometry.coordinates.map((c) => [...c] as [number, number]),
      },
      properties: {
        id: edge.id,
        type: 'edge',
        roadClass: edge.roadClass,
        fromNodeId: edge.fromNode.id,
        toNodeId: edge.toNode.id,
        lengthMeters: edge.lengthMeters,
        edgeVersion: (edge as any).edgeVersion || 1,
      },
    }));

    const beforeState: MoveNodeStateSnapshot = {
      nodeFeature: beforeNodeFeature,
      edgeFeatures: beforeEdgeFeatures,
    };

    console.log('[DIAG:MOVE] beforeState captured:', {
      nodeId: beforeNodeFeature.properties.id,
      originalPosition: beforeNodeFeature.geometry.coordinates,
      edgesCount: beforeEdgeFeatures.length,
      edgesWithShapePoints: beforeEdgeFeatures.filter((e) => e.geometry.coordinates.length > 2).length,
    });

    try {
      console.log('[NetworkEditContext] ⏳ Updating node position...');

      const newPosition = state.previewNode.geometry;
      const newCoords = newPosition.coordinates as [number, number];

      // 1. Actualizar geometría del nodo en la BD
      const updatedNode = await canonicalNetworkApi.updateNode(state.selectedNodeId, {
        geometry: newPosition,
      });

      console.log('[NetworkEditContext] ✅ Node position updated:', state.selectedNodeId);

      // ═══════════════════════════════════════════════════════════════════════
      // BUG 1 FIX: Usar state.connectedEdges (original) para PRESERVAR shape points
      // ═══════════════════════════════════════════════════════════════════════
      const updatedEdges: any[] = [];

      if (state.connectedEdges && state.connectedEdges.length > 0) {
        console.log('[NetworkEditContext] ⏳ Updating connected edges (preserving shape points)...');

        for (const originalEdge of state.connectedEdges) {
          const fromNodeId = originalEdge.fromNode.id;
          const toNodeId = originalEdge.toNode.id;
          const isFromNode = fromNodeId === state.selectedNodeId;

          // BUG 1 FIX: Preservar shape points - clonar coordenadas originales
          const originalCoords = originalEdge.geometry.coordinates;
          const newEdgeCoords: [number, number][] = originalCoords.map((c) => [...c] as [number, number]);

          // Solo modificar el vértice correspondiente al nodo movido
          if (isFromNode) {
            // El nodo movido es fromNode → actualizar PRIMER vértice
            newEdgeCoords[0] = newCoords;
          } else {
            // El nodo movido es toNode → actualizar ÚLTIMO vértice
            newEdgeCoords[newEdgeCoords.length - 1] = newCoords;
          }

          console.log('[DIAG:MOVE] Edge shape points preserved:', {
            edgeId: originalEdge.id,
            isFromNode,
            originalVertices: originalCoords.length,
            newVertices: newEdgeCoords.length,
            shapePointsPreserved: originalCoords.length > 2 ? originalCoords.length - 2 : 0,
          });

          // Actualizar edge en la BD con geometría que preserva shape points
          const updatedEdge = await canonicalNetworkApi.updateEdge(originalEdge.id, {
            geometry: {
              type: 'LineString',
              coordinates: newEdgeCoords,
            },
          });

          updatedEdges.push(updatedEdge);
          console.log('[NetworkEditContext] ✅ Edge updated (shape points preserved):', originalEdge.id);
        }
      }

      // ═══════════════════════════════════════════════════════════════════════
      // BUG 2 FIX: Construir afterState desde respuestas REALES del backend
      // ═══════════════════════════════════════════════════════════════════════
      const afterNodeFeature = rawToFeature(updatedNode) as CanonicalNodeFeature;
      const afterEdgeFeatures = updatedEdges.map((e) => rawToFeature(e)).filter(Boolean) as CanonicalEdgeFeature[];

      const afterState: MoveNodeStateSnapshot = {
        nodeFeature: afterNodeFeature,
        edgeFeatures: afterEdgeFeatures,
      };

      console.log('[DIAG:MOVE] afterState built from API response:', {
        nodeId: afterNodeFeature.properties.id,
        newPosition: afterNodeFeature.geometry.coordinates,
        edgesCount: afterEdgeFeatures.length,
      });

      // 3. Actualizar store canónico con las features actualizadas
      console.log('[NetworkEditContext] ⏳ Updating canonical store...');
      canonicalNetworkStore.updateAndSync([afterNodeFeature, ...afterEdgeFeatures]);

      console.log('[DIAG:MOVE] Store updated:', canonicalNetworkStore.getStats());

      // 4. Remover feature-state 'hidden' de nodo y edges originales
      console.log('[NetworkEditContext] 🧹 Removing hidden state from moved features...');
      const map = mapEngine.getMap();
      if (map) {
        if (state.selectedNodeId) {
          map.removeFeatureState(
            { source: 'canonical-network', id: state.selectedNodeId },
            'hidden'
          );
        }
        if (state.connectedEdgeIds && state.connectedEdgeIds.length > 0) {
          state.connectedEdgeIds.forEach((edgeId) => {
            map.removeFeatureState(
              { source: 'canonical-network', id: edgeId },
              'hidden'
            );
          });
        }
      }

      // ═══════════════════════════════════════════════════════════════════════
      // BUG 2 FIX: Registrar MoveNodeCommand para undo/redo
      // ═══════════════════════════════════════════════════════════════════════
      const moveCommand = new MoveNodeCommand({
        projectId,
        nodeId: state.selectedNodeId,
        beforeState,
        afterState,
      });

      networkUndoRedoService.registerCommand(moveCommand);

      console.log('[DIAG:MOVE] MoveNodeCommand registered:', {
        commandId: moveCommand.id,
        canUndo: networkUndoRedoService.canUndo(),
        undoStackSize: networkUndoRedoService.getState().undoCount,
      });

      // 5. Limpiar estado
      setState((prev) => ({
        ...prev,
        editMode: null,
        selectedNodeId: null,
        originalNodePosition: null,
        connectedEdgeIds: null,
        connectedEdges: null,
        movingNode: null,
        isDragging: false,
        previewNode: null,
        previewEdge: null,
        previewEdge2: null,
        previewEdges: [],
      }));

      // FASE 5.1: Limpiar feature-states después de operación
      cleanupFeatureStates();

      console.log('[NetworkEditContext] ✅ Node move completed successfully');
      console.log('[DIAG:MOVE] ═══════════════════════════════════════');
    } catch (error) {
      console.error('[NetworkEditContext] ❌ Error moving node:', error);
      alert('Error al mover nodo. Ver consola para detalles.');
    }
  }, [state.selectedNodeId, state.previewNode, state.connectedEdgeIds, state.connectedEdges, state.movingNode, state.originalNodePosition, projectId]);

  /**
   * Cancela movimiento y vuelve a posición original
   */
  const cancelNodeMove = useCallback(() => {
    console.log('[NetworkEditContext] cancelNodeMove');

    // Remover feature-state 'hidden' de nodo y edges originales
    const map = mapEngine.getMap();
    if (map) {
      if (state.selectedNodeId) {
        map.removeFeatureState(
          { source: 'canonical-network', id: state.selectedNodeId },
          'hidden'
        );
      }
      if (state.connectedEdgeIds && state.connectedEdgeIds.length > 0) {
        state.connectedEdgeIds.forEach((edgeId) => {
          map.removeFeatureState(
            { source: 'canonical-network', id: edgeId },
            'hidden'
          );
        });
      }
    }

    setState((prev) => ({
      ...prev,
      editMode: null,
      selectedNodeId: null,
      originalNodePosition: null,
      connectedEdgeIds: null,
      isDragging: false,
      previewNode: null,
      previewEdge: null,
      previewEdge2: null,
      previewEdges: [],
    }));

    // FASE 5.1: Limpiar feature-states al cancelar
    cleanupFeatureStates();
  }, [cleanupFeatureStates, state.selectedNodeId, state.connectedEdgeIds]);

  // ── Acciones: Edición Geométrica de Segmento ────────────────

  /**
   * Inicia edición geométrica de un edge
   * Guarda geometría original y prepara para mostrar handles internos
   */
  const startGeometryEdit = useCallback((edgeId: string) => {
    console.log('[NetworkEditContext] startGeometryEdit:', edgeId);

    // Obtener edge desde el store
    const edgeFeature = canonicalNetworkStore.getEdge(edgeId);
    if (!edgeFeature) {
      console.error('[NetworkEditContext] Edge not found in store:', edgeId);
      return;
    }

    const coords = edgeFeature.geometry.coordinates;
    if (coords.length <= 2) {
      console.log('[NetworkEditContext] Edge has no internal shape points:', edgeId);
    }

    // Clonar coordenadas originales para undo
    const originalCoords = coords.map((c) => [...c] as [number, number]);

    // PERF: Inicializar coords de preview (se usarán durante drag en vez del store)
    dragPreviewCoordsRef.current = originalCoords.map((c) => [...c] as [number, number]);

    console.log('[NetworkEditContext] Geometry edit started:', {
      edgeId,
      totalVertices: coords.length,
      internalVertices: Math.max(0, coords.length - 2),
    });

    // CRÍTICO: Actualizar ref inmediatamente para evitar stale closures
    geometryEditEdgeIdRef.current = edgeId;

    // NOTA: NO ocultamos el edge canónico. Permanece visible como referencia.
    // El preview source se usa solo durante drag activo para mostrar posición nueva.

    setState((prev) => ({
      ...prev,
      geometryEditEdgeId: edgeId,
      geometryEditOriginalCoords: originalCoords,
      selectedEdgeId: edgeId,
    }));
  }, []);

  /**
   * Inicia drag de un shape point interno
   */
  const startShapePointDrag = useCallback((shapePointIndex: number) => {
    if (!state.geometryEditEdgeId) {
      console.error('[NetworkEditContext] Cannot start shape point drag: no edge in geometry edit mode');
      return;
    }

    console.log('[NetworkEditContext] startShapePointDrag:', {
      edgeId: state.geometryEditEdgeId,
      shapePointIndex,
    });

    // CRÍTICO: Actualizar ref INMEDIATAMENTE para que updateShapePointPosition
    // tenga el valor correcto sin esperar re-render
    draggingShapePointIndexRef.current = shapePointIndex;

    // ═══════════════════════════════════════════════════════════════════════
    // FIX: Ocultar edge original durante drag para evitar duplicación visual
    // El preview source mostrará la geometría actualizada en tiempo real
    // ═══════════════════════════════════════════════════════════════════════
    const map = mapEngine.getMap();
    if (map) {
      // 1. Inicializar preview source con geometría actual ANTES de ocultar el edge
      //    Esto evita un momento sin línea visible
      const currentCoords = dragPreviewCoordsRef.current;
      if (currentCoords) {
        const previewSource = map.getSource('edge-geometry-preview') as GeoJSONSource;
        if (previewSource) {
          previewSource.setData({
            type: 'FeatureCollection',
            features: [{
              type: 'Feature',
              id: state.geometryEditEdgeId,
              geometry: { type: 'LineString', coordinates: currentCoords },
              properties: { id: state.geometryEditEdgeId, type: 'edge', isPreview: true },
            }],
          });
          console.log('[NetworkEditContext] Preview initialized with current geometry');
        }
      }

      // 2. Ocultar edge original
      map.setFeatureState(
        { source: 'canonical-network', id: state.geometryEditEdgeId },
        { hidden: true }
      );
      console.log('[NetworkEditContext] Edge hidden for drag preview:', state.geometryEditEdgeId);
    }

    setState((prev) => ({
      ...prev,
      draggingShapePointIndex: shapePointIndex,
      isDragging: true,
    }));
  }, [state.geometryEditEdgeId]);

  /**
   * Actualiza posición de shape point durante drag
   *
   * PERF OPTIMIZADO:
   * - NO toca canonicalNetworkStore durante drag
   * - NO actualiza canonical-network source (19k features)
   * - Solo actualiza refs y sources livianos (preview + handles)
   * - El store se actualiza UNA SOLA VEZ en confirmGeometryEdit
   */
  const updateShapePointPosition = useCallback((newPosition: [number, number]) => {
    // Guardar la última posición
    latestShapePointPositionRef.current = newPosition;

    // Si ya hay un frame pendiente, no programar otro
    if (pendingShapePointUpdateRef.current !== null) {
      return;
    }

    // Programar actualización para el próximo frame
    pendingShapePointUpdateRef.current = requestAnimationFrame(() => {
      // ═══════════════════════════════════════════════════════════════════
      // PERF: Medir tiempo de cada frame
      // ═══════════════════════════════════════════════════════════════════
      const frameStart = performance.now();

      pendingShapePointUpdateRef.current = null;

      const position = latestShapePointPositionRef.current;
      if (!position) return;

      const edgeId = geometryEditEdgeIdRef.current;
      const shapePointIndex = draggingShapePointIndexRef.current;

      if (!edgeId || shapePointIndex === null) {
        return;
      }

      // ═══════════════════════════════════════════════════════════════════
      // PERF: Usar coords del ref de preview, NO del store canónico
      // ═══════════════════════════════════════════════════════════════════
      const currentCoords = dragPreviewCoordsRef.current;
      if (!currentCoords) {
        console.warn('[PERF] dragPreviewCoordsRef is null, cannot update');
        return;
      }

      // ═══════════════════════════════════════════════════════════════════
      // TIMING BREAKDOWN: Medir cada operación
      // ═══════════════════════════════════════════════════════════════════
      const t0 = performance.now();

      // Actualizar coords en el ref local (NO en store)
      const newCoords = currentCoords.map((c, i) =>
        i === shapePointIndex ? position : [...c] as [number, number]
      );
      dragPreviewCoordsRef.current = newCoords;

      const t1 = performance.now();

      const map = mapEngine.getMap();
      if (!map) return;

      const t2 = performance.now();

      // ═══════════════════════════════════════════════════════════════════
      // PERF: Solo actualizar preview source (1 feature) - NO canonical (19k)
      // ═══════════════════════════════════════════════════════════════════
      const previewSource = map.getSource('edge-geometry-preview') as GeoJSONSource;
      if (previewSource) {
        previewSource.setData({
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            id: edgeId,
            geometry: { type: 'LineString', coordinates: newCoords },
            properties: { id: edgeId, type: 'edge', isPreview: true },
          }],
        });
      }

      const t3 = performance.now();

      // ═══════════════════════════════════════════════════════════════════
      // Actualizar handles (~10 features) - esto es barato
      // ═══════════════════════════════════════════════════════════════════
      const handlesSource = map.getSource('edge-shape-handles') as GeoJSONSource;
      if (handlesSource) {
        const shapePointFeatures: GeoJSON.Feature[] = [];
        for (let i = 1; i < newCoords.length - 1; i++) {
          shapePointFeatures.push({
            type: 'Feature',
            id: `${edgeId}-sp-${i}`,
            geometry: { type: 'Point', coordinates: newCoords[i] },
            properties: { edgeId, shapePointIndex: i, isShapePoint: true },
          });
        }
        handlesSource.setData({ type: 'FeatureCollection', features: shapePointFeatures });
      }

      const t4 = performance.now();

      // ═══════════════════════════════════════════════════════════════════
      // PERF: Log tiempo de frame con breakdown detallado
      // ═══════════════════════════════════════════════════════════════════
      const frameTime = t4 - frameStart;
      if (frameTime > 5) {
        console.warn(`[PERF] ⚠️ Slow drag frame: ${frameTime.toFixed(2)}ms`, {
          coordsMap: (t1 - t0).toFixed(2),
          getMap: (t2 - t1).toFixed(2),
          previewSetData: (t3 - t2).toFixed(2),
          handlesSetData: (t4 - t3).toFixed(2),
          coordsCount: newCoords.length,
        });
      }
    }); // End requestAnimationFrame
  }, []); // Sin dependencias - usa refs

  /**
   * Confirma edición geométrica (persiste y registra undo)
   *
   * PERF: Este es el ÚNICO lugar donde se actualiza canonicalNetworkStore
   * durante la edición geométrica. El drag usa solo refs y preview sources.
   */
  const confirmGeometryEdit = useCallback(async () => {
    console.log('[NetworkEditContext] confirmGeometryEdit');

    if (!state.geometryEditEdgeId || !state.geometryEditOriginalCoords || !projectId) {
      console.error('[NetworkEditContext] Cannot confirm geometry edit: missing data');
      return;
    }

    // ═══════════════════════════════════════════════════════════════════════
    // PERF: Usar coords del ref de preview como fuente de verdad
    // El store canónico NO fue actualizado durante el drag
    // ═══════════════════════════════════════════════════════════════════════
    const afterCoords = dragPreviewCoordsRef.current;
    if (!afterCoords) {
      console.error('[NetworkEditContext] dragPreviewCoordsRef is null');
      cancelGeometryEdit();
      return;
    }

    // Verificar si hubo cambios
    const hasChanges = JSON.stringify(state.geometryEditOriginalCoords) !== JSON.stringify(afterCoords);
    if (!hasChanges) {
      console.log('[NetworkEditContext] No geometry changes to save');
      cancelGeometryEdit();
      return;
    }

    const edgeId = state.geometryEditEdgeId;
    const map = mapEngine.getMap();

    try {
      console.log('[NetworkEditContext] ⏳ Persisting geometry changes...');

      // 1. Persistir en backend
      await canonicalNetworkApi.updateEdge(edgeId, {
        geometry: {
          type: 'LineString',
          coordinates: afterCoords,
        },
      });

      console.log('[NetworkEditContext] ✅ Geometry persisted');

      // ═══════════════════════════════════════════════════════════════════════
      // PERF: AHORA sí actualizar el store canónico (UNA SOLA VEZ)
      // ═══════════════════════════════════════════════════════════════════════
      const edgeFeature = canonicalNetworkStore.getEdge(edgeId);
      if (edgeFeature) {
        const updatedFeature: CanonicalEdgeFeature = {
          ...edgeFeature,
          geometry: {
            type: 'LineString',
            coordinates: afterCoords,
          },
        };
        canonicalNetworkStore.updateAndSync([updatedFeature]);
        console.log('[NetworkEditContext] ✅ Canonical store updated (once)');
      }

      // 2. Registrar comando para undo/redo
      const command = new EditEdgeGeometryCommand({
        projectId,
        edgeId,
        beforeGeometry: {
          type: 'LineString',
          coordinates: state.geometryEditOriginalCoords,
        },
        afterGeometry: {
          type: 'LineString',
          coordinates: afterCoords,
        },
      });

      networkUndoRedoService.registerCommand(command);

      console.log('[NetworkEditContext] ✅ EditEdgeGeometryCommand registered:', {
        commandId: command.id,
        canUndo: networkUndoRedoService.canUndo(),
      });

      // ═══════════════════════════════════════════════════════════════════════
      // 3. Transición visual sin parpadeo:
      //
      // PROBLEMA RESUELTO:
      // - setData() de canonical-network (~17k features) es asíncrono
      // - map.once('render') se dispara muy pronto, antes de que el source se procese
      // - Resultado: el edge canónico aparece con geometría VIEJA momentáneamente
      //
      // SOLUCIÓN:
      // - Usar map.once('idle') que se dispara cuando MapLibre termina TODO el trabajo
      // - 'idle' garantiza que setData() fue completamente procesado
      // ═══════════════════════════════════════════════════════════════════════
      if (map) {
        // Refrescar handles inmediatamente (source pequeño, se procesa rápido)
        const handlesSource = map.getSource('edge-shape-handles') as GeoJSONSource;
        if (handlesSource) {
          const shapePointFeatures: GeoJSON.Feature[] = [];
          for (let i = 1; i < afterCoords.length - 1; i++) {
            shapePointFeatures.push({
              type: 'Feature',
              id: `${edgeId}-sp-${i}`,
              geometry: { type: 'Point', coordinates: afterCoords[i] },
              properties: { edgeId, shapePointIndex: i, isShapePoint: true },
            });
          }
          handlesSource.setData({ type: 'FeatureCollection', features: shapePointFeatures });
          console.log('[NetworkEditContext] ✅ Handles refreshed:', shapePointFeatures.length, 'points');
        }

        // CRÍTICO: Esperar a que MapLibre termine TODO el trabajo pendiente
        // 'idle' se dispara solo cuando:
        // - Todos los source updates están procesados
        // - Todas las tiles están cargadas
        // - No hay más trabajo pendiente
        await new Promise<void>((resolve) => {
          console.log('[NetworkEditContext] ⏳ Waiting for MapLibre idle...');

          map.once('idle', () => {
            console.log('[NetworkEditContext] 🏁 MapLibre idle - source fully processed');

            // AHORA el source canonical-network tiene la geometría nueva lista
            // 1. Ocultar preview (ya no necesario)
            const previewSource = map.getSource('edge-geometry-preview') as GeoJSONSource;
            if (previewSource) {
              previewSource.setData({ type: 'FeatureCollection', features: [] });
            }

            // 2. Mostrar edge canónico (garantizado que tiene geometría nueva)
            map.removeFeatureState({ source: 'canonical-network', id: edgeId });

            console.log('[NetworkEditContext] ✅ Visual transition complete (no flash)');
            resolve();
          });
        });
      }

      // 4. Limpiar refs de drag, pero MANTENER refs del modo
      if (pendingShapePointUpdateRef.current !== null) {
        cancelAnimationFrame(pendingShapePointUpdateRef.current);
        pendingShapePointUpdateRef.current = null;
      }
      latestShapePointPositionRef.current = null;
      draggingShapePointIndexRef.current = null;

      // PERF: Actualizar baseline para próxima edición (undo necesita coords originales)
      // dragPreviewCoordsRef se reinicializará en el próximo startShapePointDrag
      dragPreviewCoordsRef.current = afterCoords.map((c) => [...c] as [number, number]);

      // MANTENER MODO ACTIVO: solo limpiar estado de drag, no el modo
      setState((prev) => ({
        ...prev,
        // geometryEditEdgeId: se MANTIENE para que el modo siga activo
        geometryEditOriginalCoords: afterCoords, // Nueva baseline para próximo undo
        draggingShapePointIndex: null,
        isDragging: false,
      }));

      console.log('[NetworkEditContext] ✅ Shape point saved, mode stays active');
    } catch (error) {
      console.error('[NetworkEditContext] ❌ Error saving geometry:', error);
      alert('Error al guardar geometría. Ver consola para detalles.');
    }
  }, [state.geometryEditEdgeId, state.geometryEditOriginalCoords, projectId]);

  /**
   * Cancela edición geométrica (restaura geometría original)
   *
   * PERF: No necesita restaurar el store canónico porque nunca fue modificado
   * durante el drag. Solo limpia preview y restaura visibilidad.
   */
  const cancelGeometryEdit = useCallback(() => {
    console.log('[NetworkEditContext] 🛑 cancelGeometryEdit called, edgeId:', state.geometryEditEdgeId);

    // Cancelar cualquier actualización pendiente de requestAnimationFrame
    if (pendingShapePointUpdateRef.current !== null) {
      cancelAnimationFrame(pendingShapePointUpdateRef.current);
      pendingShapePointUpdateRef.current = null;
    }
    latestShapePointPositionRef.current = null;

    // ═════════════════════════════════════════��═════════════════════════════
    // CLEANUP COMPLETO: Limpiar TODAS las fuentes visuales
    // ═══════════════════════════════════════════════════════════════════════
    const map = mapEngine.getMap();
    if (map) {
      // 1. Limpiar handles source (shape points)
      const handlesSource = map.getSource('edge-shape-handles') as GeoJSONSource;
      if (handlesSource) {
        handlesSource.setData({ type: 'FeatureCollection', features: [] });
        console.log('[NetworkEditContext] ✓ Handles source cleared');
      }

      // 2. Limpiar preview source (edge preview)
      const previewSource = map.getSource('edge-geometry-preview') as GeoJSONSource;
      if (previewSource) {
        previewSource.setData({ type: 'FeatureCollection', features: [] });
        console.log('[NetworkEditContext] ✓ Preview source cleared');
      }

      // 3. Restaurar visibilidad del edge canónico si estaba oculto
      if (state.geometryEditEdgeId) {
        map.removeFeatureState({ source: 'canonical-network', id: state.geometryEditEdgeId });
        console.log('[NetworkEditContext] ✓ Edge visibility restored');
      }
    }

    // Limpiar refs
    dragPreviewCoordsRef.current = null;
    geometryEditEdgeIdRef.current = null;
    draggingShapePointIndexRef.current = null;

    setState((prev) => ({
      ...prev,
      geometryEditEdgeId: null,
      geometryEditOriginalCoords: null,
      draggingShapePointIndex: null,
      isDragging: false,
    }));

    // ═══════════════════════════════════════════════════════════════════════
    // DIAGNOSTIC: Estado final después de salir del modo de edición geométrica
    // ═══════════════════════════════════════════════════════════════════════
    console.log('[GEOMETRY_EXIT] ════════════════════════════════════════════');
    console.log('[GEOMETRY_EXIT] ✅ FINAL STATE SNAPSHOT:');
    console.log('[GEOMETRY_EXIT]   geometryEditEdgeId: null');
    console.log('[GEOMETRY_EXIT]   geometryEditOriginalCoords: null');
    console.log('[GEOMETRY_EXIT]   draggingShapePointIndex: null');
    console.log('[GEOMETRY_EXIT]   isDragging: false');
    console.log('[GEOMETRY_EXIT]   dragPreviewCoordsRef: null');
    console.log('[GEOMETRY_EXIT]   geometryEditEdgeIdRef: null');
    console.log('[GEOMETRY_EXIT]   handles source: cleared');
    console.log('[GEOMETRY_EXIT]   preview source: cleared');
    console.log('[GEOMETRY_EXIT] ════════════════════════════════════════════');
  }, [state.geometryEditEdgeId]);

  // ═══════════════════════════════════════════════════════════════════════════
  // SIMPLIFICAR A RECTA (Operación geométrica - NO altera topología)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Simplifica un edge a línea recta eliminando todos los shape points internos.
   * Conserva solo los nodos extremos (fromNode y toNode).
   *
   * @param edgeId - ID del edge a simplificar
   *
   * @remarks
   * - Operación GEOMÉTRICA: no altera topología de la red
   * - Si hay geometry edit activo, lo cancela primero
   * - Registra comando para undo (reutiliza EditEdgeGeometryCommand)
   * - Si el edge ya es recto (2 puntos), no hace nada
   */
  const simplifyEdgeToLine = useCallback(async (edgeId: string) => {
    console.log('[NetworkEditContext] 📏 simplifyEdgeToLine:', edgeId);

    if (!projectId) {
      console.error('[NetworkEditContext] No projectId available');
      return;
    }

    // ═══════════════════════════════════════════════════════════════════════
    // CLEANUP: Cancelar geometry edit si está activo (requisito B)
    // ═══════════════════════════════════════════════════════════════════════
    const map = mapEngine.getMap();

    if (geometryEditEdgeIdRef.current) {
      console.log('[NetworkEditContext] ⚠️ Cancelling active geometry edit before simplify');

      // Limpiar sources visuales
      if (map) {
        const handlesSource = map.getSource('edge-shape-handles') as GeoJSONSource;
        if (handlesSource) handlesSource.setData({ type: 'FeatureCollection', features: [] });

        const previewSource = map.getSource('edge-geometry-preview') as GeoJSONSource;
        if (previewSource) previewSource.setData({ type: 'FeatureCollection', features: [] });

        // Restaurar visibilidad del edge
        map.removeFeatureState({ source: 'canonical-network', id: geometryEditEdgeIdRef.current });
      }

      // Limpiar refs
      dragPreviewCoordsRef.current = null;
      geometryEditEdgeIdRef.current = null;
      draggingShapePointIndexRef.current = null;
    }

    // Obtener edge actual del store
    const edgeFeature = canonicalNetworkStore.getEdge(edgeId);
    if (!edgeFeature) {
      console.error('[NetworkEditContext] Edge not found in store:', edgeId);
      return;
    }

    const currentCoords = edgeFeature.geometry.coordinates as Array<[number, number]>;

    // Verificar si ya es recto (solo 2 puntos)
    if (currentCoords.length <= 2) {
      console.log('[NetworkEditContext] ⏭️ Edge is already straight, nothing to simplify');
      return;
    }

    // Guardar geometría original para undo
    const beforeGeometry = {
      type: 'LineString' as const,
      coordinates: [...currentCoords] as Array<[number, number]>,
    };

    // Nueva geometría: solo primer y último punto
    const afterGeometry = {
      type: 'LineString' as const,
      coordinates: [currentCoords[0], currentCoords[currentCoords.length - 1]] as Array<[number, number]>,
    };

    console.log('[NetworkEditContext] 📏 Simplifying edge:', {
      edgeId,
      beforePoints: currentCoords.length,
      afterPoints: 2,
    });

    try {
      // Cambiar cursor a espera
      if (map) {
        map.getCanvas().style.cursor = 'wait';
      }

      // 1. Actualizar en backend
      const updatedEdge = await canonicalNetworkApi.updateEdge(edgeId, {
        geometry: afterGeometry,
      });

      console.log('[NetworkEditContext] ✅ Edge simplified in backend:', updatedEdge.id);

      // 2. Actualizar store y sincronizar
      const updatedFeature: CanonicalEdgeFeature = {
        type: 'Feature',
        id: updatedEdge.id,
        geometry: updatedEdge.geometry as GeoJSON.LineString,
        properties: {
          id: updatedEdge.id,
          type: 'edge',
          fromNodeId: updatedEdge.fromNode.id,
          toNodeId: updatedEdge.toNode.id,
          roadClass: updatedEdge.roadClass || null,
          lengthMeters: updatedEdge.lengthMeters || null,
          edgeVersion: (updatedEdge as any).edgeVersion || 1,
        },
      };

      canonicalNetworkStore.updateAndSync([updatedFeature]);
      console.log('[NetworkEditContext] ✅ Store updated');

      // 3. Registrar comando para undo (reutiliza EditEdgeGeometryCommand)
      const undoCommand = new EditEdgeGeometryCommand({
        projectId,
        edgeId,
        beforeGeometry,
        afterGeometry,
      });
      networkUndoRedoService.registerCommand(undoCommand);
      console.log('[NetworkEditContext] ✅ Undo command registered for simplify');

      // 4. Limpiar estado de geometry edit completamente
      setState((prev) => ({
        ...prev,
        geometryEditEdgeId: null,
        geometryEditOriginalCoords: null,
        draggingShapePointIndex: null,
        edgeContextMenu: null, // Cerrar menú contextual si estaba abierto
      }));

    } catch (error) {
      console.error('[NetworkEditContext] ❌ Error simplifying edge:', error);
      alert('Error al simplificar el segmento. Intente nuevamente.');
    } finally {
      // Restaurar cursor
      if (map) {
        map.getCanvas().style.cursor = '';
      }
    }
  }, [projectId]);

  // ═══════════════════════════════════════════════════════════════════════════
  // AGREGAR / ELIMINAR SHAPE POINTS (Operaciones geométricas)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Refresca los handles visuales para reflejar el estado actual del edge.
   * Útil después de agregar/eliminar shape points.
   */
  const refreshShapeHandles = useCallback(() => {
    const edgeId = geometryEditEdgeIdRef.current;
    if (!edgeId) return;

    const map = mapEngine.getMap();
    if (!map) return;

    const source = map.getSource('edge-shape-handles') as GeoJSONSource;
    if (!source) return;

    const edgeFeature = canonicalNetworkStore.getEdge(edgeId);
    if (!edgeFeature) return;

    const coords = edgeFeature.geometry.coordinates;
    const shapePointFeatures: GeoJSON.Feature[] = [];

    // Generar handles para vértices internos (excluyendo extremos)
    for (let i = 1; i < coords.length - 1; i++) {
      shapePointFeatures.push({
        type: 'Feature',
        id: `${edgeId}-sp-${i}`,
        geometry: { type: 'Point', coordinates: coords[i] },
        properties: { edgeId, shapePointIndex: i, isShapePoint: true },
      });
    }

    source.setData({ type: 'FeatureCollection', features: shapePointFeatures });
    console.log('[NetworkEditContext] 🔄 Handles refreshed:', shapePointFeatures.length, 'points');
  }, []);

  /**
   * Agrega un shape point geométrico al edge en edición.
   * El punto se inserta en el segmento más cercano a la posición dada.
   *
   * @param position - Coordenadas [lng, lat] donde insertar el punto
   * @returns Índice del nuevo shape point, o null si falló
   */
  const addShapePoint = useCallback(async (position: [number, number]): Promise<number | null> => {
    const edgeId = geometryEditEdgeIdRef.current;
    if (!edgeId) {
      console.error('[NetworkEditContext] addShapePoint: No edge in geometry edit mode');
      return null;
    }

    if (!projectId) {
      console.error('[NetworkEditContext] addShapePoint: No projectId');
      return null;
    }

    // Obtener edge actual del store
    const edgeFeature = canonicalNetworkStore.getEdge(edgeId);
    if (!edgeFeature) {
      console.error('[NetworkEditContext] addShapePoint: Edge not found');
      return null;
    }

    const currentCoords = edgeFeature.geometry.coordinates as Array<[number, number]>;

    // Guardar geometría original para undo
    const beforeGeometry = {
      type: 'LineString' as const,
      coordinates: [...currentCoords] as Array<[number, number]>,
    };

    // Encontrar en qué segmento insertar el nuevo punto
    let insertIndex = 1; // Por defecto, después del primer punto
    let minDistance = Infinity;

    for (let i = 0; i < currentCoords.length - 1; i++) {
      const p1 = currentCoords[i];
      const p2 = currentCoords[i + 1];

      // Calcular distancia del punto al segmento
      const dist = pointToSegmentDistance(position, p1, p2);

      if (dist < minDistance) {
        minDistance = dist;
        insertIndex = i + 1; // Insertar después de p1
      }
    }

    // Crear nueva geometría con el punto insertado
    const newCoords = [
      ...currentCoords.slice(0, insertIndex),
      position,
      ...currentCoords.slice(insertIndex),
    ];

    const afterGeometry = {
      type: 'LineString' as const,
      coordinates: newCoords as Array<[number, number]>,
    };

    console.log('[NetworkEditContext] ➕ addShapePoint:', {
      position,
      insertIndex,
      beforePoints: currentCoords.length,
      afterPoints: newCoords.length,
    });

    try {
      // 1. Actualizar en backend
      const updatedEdge = await canonicalNetworkApi.updateEdge(edgeId, {
        geometry: afterGeometry,
      });

      // 2. Actualizar store
      const updatedFeature: CanonicalEdgeFeature = {
        type: 'Feature',
        id: updatedEdge.id,
        geometry: updatedEdge.geometry as GeoJSON.LineString,
        properties: {
          id: updatedEdge.id,
          type: 'edge',
          fromNodeId: updatedEdge.fromNode.id,
          toNodeId: updatedEdge.toNode.id,
          roadClass: updatedEdge.roadClass || null,
          lengthMeters: updatedEdge.lengthMeters || null,
          edgeVersion: (updatedEdge as any).edgeVersion || 1,
        },
      };

      canonicalNetworkStore.updateAndSync([updatedFeature]);

      // 3. Registrar comando para undo
      const undoCommand = new EditEdgeGeometryCommand({
        projectId,
        edgeId,
        beforeGeometry,
        afterGeometry,
      });
      networkUndoRedoService.registerCommand(undoCommand);

      // 4. Refrescar handles
      refreshShapeHandles();

      // 5. CRÍTICO: Actualizar refs para que el próximo drag use las coordenadas correctas
      //    Sin esto, el drag usaría las coords VIEJAS y el punto agregado desaparecería
      dragPreviewCoordsRef.current = newCoords.map((c) => [...c] as [number, number]);

      // 6. Actualizar geometryEditOriginalCoords para que el undo del próximo drag sea correcto
      setState((prev) => ({
        ...prev,
        geometryEditOriginalCoords: newCoords.map((c) => [...c] as [number, number]),
      }));

      console.log('[NetworkEditContext] ✅ Shape point added at index', insertIndex);
      console.log('[NetworkEditContext] 📍 dragPreviewCoordsRef updated:', newCoords.length, 'points');
      return insertIndex;

    } catch (error) {
      console.error('[NetworkEditContext] ❌ Error adding shape point:', error);
      return null;
    }
  }, [projectId, refreshShapeHandles]);

  /**
   * Elimina un shape point geométrico del edge en edición.
   * No puede eliminar si quedarían menos de 2 puntos (los extremos).
   *
   * @param shapePointIndex - Índice del shape point a eliminar (1..n-2)
   */
  const removeShapePoint = useCallback(async (shapePointIndex: number): Promise<void> => {
    const edgeId = geometryEditEdgeIdRef.current;
    if (!edgeId) {
      console.error('[NetworkEditContext] removeShapePoint: No edge in geometry edit mode');
      return;
    }

    if (!projectId) {
      console.error('[NetworkEditContext] removeShapePoint: No projectId');
      return;
    }

    // Obtener edge actual del store
    const edgeFeature = canonicalNetworkStore.getEdge(edgeId);
    if (!edgeFeature) {
      console.error('[NetworkEditContext] removeShapePoint: Edge not found');
      return;
    }

    const currentCoords = edgeFeature.geometry.coordinates as Array<[number, number]>;

    // Validar que no quedarían menos de 2 puntos
    if (currentCoords.length <= 2) {
      console.warn('[NetworkEditContext] removeShapePoint: Cannot remove, only 2 points left');
      return;
    }

    // Validar índice
    if (shapePointIndex < 1 || shapePointIndex >= currentCoords.length - 1) {
      console.error('[NetworkEditContext] removeShapePoint: Invalid index', shapePointIndex);
      return;
    }

    // Guardar geometría original para undo
    const beforeGeometry = {
      type: 'LineString' as const,
      coordinates: [...currentCoords] as Array<[number, number]>,
    };

    // Crear nueva geometría sin el punto
    const newCoords = [
      ...currentCoords.slice(0, shapePointIndex),
      ...currentCoords.slice(shapePointIndex + 1),
    ];

    const afterGeometry = {
      type: 'LineString' as const,
      coordinates: newCoords as Array<[number, number]>,
    };

    console.log('[NetworkEditContext] ➖ removeShapePoint:', {
      shapePointIndex,
      beforePoints: currentCoords.length,
      afterPoints: newCoords.length,
    });

    try {
      // 1. Actualizar en backend
      const updatedEdge = await canonicalNetworkApi.updateEdge(edgeId, {
        geometry: afterGeometry,
      });

      // 2. Actualizar store
      const updatedFeature: CanonicalEdgeFeature = {
        type: 'Feature',
        id: updatedEdge.id,
        geometry: updatedEdge.geometry as GeoJSON.LineString,
        properties: {
          id: updatedEdge.id,
          type: 'edge',
          fromNodeId: updatedEdge.fromNode.id,
          toNodeId: updatedEdge.toNode.id,
          roadClass: updatedEdge.roadClass || null,
          lengthMeters: updatedEdge.lengthMeters || null,
          edgeVersion: (updatedEdge as any).edgeVersion || 1,
        },
      };

      canonicalNetworkStore.updateAndSync([updatedFeature]);

      // 3. Registrar comando para undo
      const undoCommand = new EditEdgeGeometryCommand({
        projectId,
        edgeId,
        beforeGeometry,
        afterGeometry,
      });
      networkUndoRedoService.registerCommand(undoCommand);

      // 4. Refrescar handles
      refreshShapeHandles();

      // 5. CRÍTICO: Actualizar refs para que el próximo drag use las coordenadas correctas
      //    Sin esto, el drag usaría las coords VIEJAS con el punto que ya no existe
      dragPreviewCoordsRef.current = newCoords.map((c) => [...c] as [number, number]);

      // 6. Actualizar geometryEditOriginalCoords para que el undo del próximo drag sea correcto
      setState((prev) => ({
        ...prev,
        geometryEditOriginalCoords: newCoords.map((c) => [...c] as [number, number]),
      }));

      console.log('[NetworkEditContext] ✅ Shape point removed at index', shapePointIndex);
      console.log('[NetworkEditContext] 📍 dragPreviewCoordsRef updated:', newCoords.length, 'points');

    } catch (error) {
      console.error('[NetworkEditContext] ❌ Error removing shape point:', error);
    }
  }, [projectId, refreshShapeHandles]);

  // ── Acciones: General ───────────────────────────────────────

  /**
   * Cancela operación actual
   */
  const cancelEdit = useCallback(() => {
    console.log('[NetworkEditContext] cancelEdit');

    // PERF: Limpiar cache de coordenadas del nodo origen
    branchSourceCoordsRef.current = null;

    setState(INITIAL_STATE);

    // Restaurar cursor del mapa
    const map = mapEngine.getMap();
    if (map) {
      map.getCanvas().style.cursor = '';
    }
  }, []);

  /**
   * Reset completo del estado
   */
  // ── Métodos de Activación de Modos ─────────────────────────

  /**
   * Activa modo crear rama
   * Usuario debe hacer click en un nodo para iniciar
   */
  const activateCreateBranchMode = useCallback(() => {
    console.log('[NetworkEditContext] Activando modo crear rama');
    setState((prev) => ({
      ...prev,
      editMode: 'create-branch',
    }));

    // Cambiar cursor del mapa
    const map = mapEngine.getMap();
    if (map) {
      map.getCanvas().style.cursor = 'crosshair';
    }
  }, []);

  /**
   * Activa modo dividir segmento
   * Usuario debe hacer click en un edge para dividirlo
   */
  const activateSplitSegmentMode = useCallback(() => {
    console.log('[NetworkEditContext] Activando modo dividir segmento');
    setState((prev) => ({
      ...prev,
      editMode: 'split-segment',
    }));

    // Cambiar cursor del mapa
    const map = mapEngine.getMap();
    if (map) {
      map.getCanvas().style.cursor = 'crosshair';
    }
  }, []);

  /**
   * Activa modo eliminar nodo
   * Usuario debe hacer click derecho en un nodo para eliminarlo
   */
  const activateDeleteNodeMode = useCallback(() => {
    console.log('[NetworkEditContext] Activando modo eliminar nodo');
    setState((prev) => ({
      ...prev,
      editMode: 'delete-node',
    }));

    // Cambiar cursor del mapa
    const map = mapEngine.getMap();
    if (map) {
      map.getCanvas().style.cursor = 'not-allowed';
    }
  }, []);

  /**
   * Activa modo mover nodo
   * Usuario debe arrastrar un nodo para moverlo
   */
  const activateMoveNodeMode = useCallback(() => {
    console.log('[NetworkEditContext] Activando modo mover nodo');
    setState((prev) => ({
      ...prev,
      editMode: 'move-node',
    }));

    // Cambiar cursor del mapa
    const map = mapEngine.getMap();
    if (map) {
      map.getCanvas().style.cursor = 'move';
    }
  }, []);

  const reset = useCallback(() => {
    console.log('[NetworkEditContext] reset');

    // PERF: Limpiar cache de coordenadas del nodo origen
    branchSourceCoordsRef.current = null;

    setState(INITIAL_STATE);

    // Restaurar cursor del mapa
    const map = mapEngine.getMap();
    if (map) {
      map.getCanvas().style.cursor = '';
    }
  }, []);

  // REFACTOR: setHoveredNodeId y setHoveredEdgeId eliminados
  // El hover ahora se gestiona exclusivamente en UrbanNetworkContext

  /**
   * Set selección de nodo (FASE 4: fuente única)
   */
  const setSelectedNodeId = useCallback((nodeId: string | null) => {
    setState((prev) => ({
      ...prev,
      selectedNodeId: nodeId,
    }));
  }, []);

  /**
   * Set selección de edge (FASE 4: fuente única)
   */
  const setSelectedEdgeId = useCallback((edgeId: string | null) => {
    setState((prev) => ({
      ...prev,
      selectedEdgeId: edgeId,
    }));
  }, []);

  /**
   * Set snap target durante creación de rama
   */
  const setSnapTarget = useCallback(
    (target: { type: 'node' | 'edge'; id: string; snapPoint: [number, number] } | null) => {
      setState((prev) => ({
        ...prev,
        snapTarget: target,
      }));
    },
    []
  );

  // ── Undo/Redo ──────────────────────────────────────────────

  /**
   * Deshace la última operación
   */
  const undo = useCallback(async () => {
    console.log('[NetworkEditContext] Undo');
    try {
      await networkUndoRedoService.undo();

      // Refrescar mapa después del undo
      // El servicio ya actualiza el mapa, pero podemos hacer cualquier limpieza adicional aquí
      setState((prev) => ({
        ...prev,
        previewNode: null,
        previewEdge: null,
        editMode: null,
      }));

      // FASE 5.1: Limpiar feature-states después de undo
      cleanupFeatureStates();
    } catch (error) {
      console.error('[NetworkEditContext] Error undoing:', error);
      alert('Error al deshacer. Ver consola para detalles.');
    }
  }, [cleanupFeatureStates]);

  /**
   * Rehace la última operación deshecha
   */
  const redo = useCallback(async () => {
    console.log('[NetworkEditContext] Redo');
    try {
      await networkUndoRedoService.redo();
    } catch (error) {
      console.error('[NetworkEditContext] Error redoing:', error);
      alert('Error al rehacer. Ver consola para detalles.');
    }
  }, []);

  /**
   * Verifica si se puede deshacer
   */
  const canUndo = useCallback(() => {
    return networkUndoRedoService.canUndo();
  }, []);

  /**
   * Verifica si se puede rehacer
   */
  const canRedo = useCallback(() => {
    return networkUndoRedoService.canRedo();
  }, []);

  // ── Valor del Contexto ──────────────────────────────────────

  const value: NetworkEditContextType = {
    // State
    ...state,
    hasPendingNetworkChanges,
    canUndoNow,
    canRedoNow,
    undoDescription: undoRedoState.undoDescription,
    redoDescription: undoRedoState.redoDescription,

    // Actions
    selectNodeForBranch,
    startBranchCreation,
    createBranchPreview,
    confirmBranchCreation,
    startSegmentSplit,
    confirmSegmentSplit,
    promptDeleteNode,
    confirmDeleteNode,
    promptDeleteEdge,
    confirmDeleteEdge,
    mergeEdges,
    toggleEdgeForMerge,
    clearMergeSelection,
    // Multi-Merge V1 (Fase 3)
    addEdgeToSelection,
    removeEdgeFromSelection,
    clearSelection,
    confirmMultiMerge,
    // Draw Freehand Segment (Etapa 1 + Etapa 2)
    startFreehandSegmentDrawing,
    addFreehandShapePoint,
    updateFreehandPreview,
    cancelFreehandSegmentDrawing,
    confirmFreehandSegment,
    openEdgeContextMenu,
    closeEdgeContextMenu,
    openNodeContextMenu,
    closeNodeContextMenu,
    startInsertNodeOnEdge,
    updateInsertNodePosition,
    confirmInsertNode,
    startNodeMove,
    updateNodePosition,
    confirmNodeMove,
    cancelNodeMove,
    activateCreateBranchMode,
    activateSplitSegmentMode,
    activateDeleteNodeMode,
    activateMoveNodeMode,
    cancelEdit,
    reset,
    // REFACTOR: setHoveredNodeId y setHoveredEdgeId movidos a UrbanNetworkContext
    setSelectedNodeId,
    setSelectedEdgeId,
    setSnapTarget,
    undo,
    redo,
    canUndo,
    canRedo,
    // Edición geométrica de segmento
    startGeometryEdit,
    startShapePointDrag,
    updateShapePointPosition,
    confirmGeometryEdit,
    cancelGeometryEdit,
    simplifyEdgeToLine,
    addShapePoint,
    removeShapePoint,
    refreshShapeHandles,
  };

  return <NetworkEditContext.Provider value={value}>{children}</NetworkEditContext.Provider>;
}

// ═══════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════

/**
 * Hook para acceder al contexto de edición de red
 *
 * @returns Valor del contexto
 * @throws Error si se usa fuera del Provider
 *
 * @example
 * ```typescript
 * const { editMode, startBranchCreation } = useNetworkEdit();
 *
 * // Click en nodo
 * startBranchCreation(nodeId);
 * ```
 */
export function useNetworkEdit(): NetworkEditContextType {
  const context = useContext(NetworkEditContext);
  if (!context) {
    throw new Error('useNetworkEdit must be used within NetworkEditProvider');
  }
  return context;
}

/**
 * Hook "safe" para acceder al contexto de edición de red
 *
 * @returns Valor del contexto o null si no está disponible
 */
export function useNetworkEditSafe(): NetworkEditContextType | null {
  return useContext(NetworkEditContext);
}
